/**
 * iter185 — Franchise Exit v2:
 *   • SettlementV2Modal — live compute of Sales + Expenses + WC + double-dip
 *     protection.  Freeze-on-Finalize.
 *   • ExitChecklistsPanel — 8 closure checklists in one accordion modal
 *     (lease · debrand · social · digital · access · confidentiality ·
 *     open matters · compliance).
 *
 * Deliberately kept small — everything talks to the new backend endpoints
 * under /api/franchise-exit/.  No local calc: the backend is source-of-truth.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Loader2, RefreshCw, Lock, Unlock, Plus, Trash2, ChevronDown, ChevronRight, AlertTriangle, CheckCircle, Save } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

async function api(path, token, body = {}) {
  const r = await fetch(`${API}/api/franchise-exit${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, ...body }),
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.detail || 'API error');
  return d;
}

/* ── Settlement Preview ────────────────────────────────────────── */
export function SettlementV2Modal({ open, onOpenChange, exitId, token, onFinalized }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null);
  const [showDrill, setShowDrill] = useState(false);
  const [addingLiab, setAddingLiab] = useState({ head: 'OTHER', description: '', amount: '', already_in_expenses: false });
  const [addingCredit, setAddingCredit] = useState({ head: 'OTHER', description: '', amount: '' });
  const [finalizing, setFinalizing] = useState(false);
  // iter288 — Reopen / Refreeze lifecycle controls
  const [showReopen, setShowReopen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [reopenAck, setReopenAck] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [showRefreeze, setShowRefreeze] = useState(false);
  const [refreezeReason, setRefreezeReason] = useState('');
  const [refreezing, setRefreezing] = useState(false);

  const load = useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try { setData(await api(`/settlement/compute/${exitId}`, token)); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [open, exitId, token]);
  useEffect(() => { load(); }, [load]);

  const loadDrill = async () => {
    try { setDrill(await api(`/settlement/expense-details/${exitId}`, token)); setShowDrill(true); }
    catch (e) { toast.error(e.message); }
  };

  const addLiab = async () => {
    if (!addingLiab.description || !addingLiab.amount) { toast.error('description + amount required'); return; }
    try {
      await api(`/settlement/liabilities/upsert-row/${exitId}`, token,
        { ...addingLiab, amount: Number(addingLiab.amount) });
      setAddingLiab({ head: 'OTHER', description: '', amount: '', already_in_expenses: false });
      load();
    } catch (e) { toast.error(e.message); }
  };
  const addCredit = async () => {
    if (!addingCredit.description || !addingCredit.amount) { toast.error('description + amount required'); return; }
    try {
      await api(`/settlement/credits/upsert-row/${exitId}`, token,
        { ...addingCredit, amount: Number(addingCredit.amount) });
      setAddingCredit({ head: 'OTHER', description: '', amount: '' });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const deleteLiab = async (rid, coll) => {
    if (!window.confirm('Remove this line?')) return;
    try { await api(`/settlement/${coll}/delete-row/${exitId}`, token, { row_id: rid }); load(); }
    catch (e) { toast.error(e.message); }
  };

  const finalize = async () => {
    if (!window.confirm('Finalize the settlement? This freezes every figure and the calculation will not silently recompute afterwards.')) return;
    setFinalizing(true);
    try {
      const r = await api(`/settlement/finalize/${exitId}`, token, { reason: 'SA finalize' });
      if (r.success) { toast.success('Settlement finalized & frozen'); onFinalized?.(); onOpenChange(false); }
      else { toast.info(r.message || 'Already finalized'); }
    } catch (e) { toast.error(e.message); }
    finally { setFinalizing(false); }
  };

  // iter288 — Reopen a frozen settlement.  Archives v1 to history and
  // transitions the UI into editable v2-draft mode.
  const doReopen = async () => {
    if (!reopenReason.trim()) { toast.error('Reason is mandatory'); return; }
    if (!reopenAck) { toast.error('Please confirm the acknowledgement'); return; }
    setReopening(true);
    try {
      await api(`/settlement/unlock/${exitId}`, token,
        { reason: reopenReason.trim(), confirmed: true });
      toast.success('Settlement reopened for correction — v1 preserved in history');
      setShowReopen(false); setReopenReason(''); setReopenAck(false);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setReopening(false); }
  };

  // iter288 — Finalise & Freeze the v2 draft.  Mints a new version and
  // leaves v1 untouched in franchise_exit_settlement_history.
  const doRefreeze = async () => {
    setRefreezing(true);
    try {
      const r = await api(`/settlement/freeze/${exitId}`, token,
        { reason: refreezeReason.trim() });
      if (r?.success) {
        toast.success(`Settlement re-frozen as v${r.snapshot?.settlement_version || '?'}`);
        setShowRefreeze(false); setRefreezeReason('');
        onFinalized?.();
        await load();
      } else {
        toast.error('Refreeze did not complete');
      }
    } catch (e) { toast.error(e.message); }
    finally { setRefreezing(false); }
  };

  if (!open) return null;
  // iter288 — tri-state source: 'frozen' (locked, SA may reopen),
  // 'reopened' (v2 draft, editable), 'live' (never finalised).
  const source = data?.source || 'live';
  const isFrozen = source === 'frozen';
  const isReopened = source === 'reopened';
  const isReadOnly = isFrozen;
  const snap = isFrozen ? data.snapshot : data;
  const c = snap?.computed || {};
  const sales = snap?.sales || {};
  const frozenVer = data?.frozen_version || snap?.settlement_version || 1;
  const nextVer = data?.next_version || (frozenVer + 1);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="settlement-v2-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Settlement Preview
            {isFrozen && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1"
                data-testid="settle-frozen-badge">
                <Lock className="w-3 h-3" /> Frozen v{frozenVer}
              </Badge>
            )}
            {isReopened && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-300 gap-1"
                data-testid="settle-reopened-badge">
                REOPENED — Draft v{nextVer}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Live pull from Sales Dashboard, Expense Details and Working Capital chain. Additional deductions flagged &quot;already in expenses&quot; are excluded to prevent double-deduction.
          </DialogDescription>
        </DialogHeader>
        {isReopened && (
          <div className="rounded-md border-2 border-amber-300 bg-amber-50 px-4 py-3 text-sm"
               data-testid="settle-reopened-banner">
            <div className="flex items-start gap-2">
              <div className="font-semibold text-amber-900">
                Settlement reopened for correction. v{frozenVer} is preserved in history.
              </div>
            </div>
            <div className="text-[11px] text-amber-800 mt-1">
              Edit the liabilities and credits below — changes save automatically. Working
              Capital / Sales / Operating Expenses continue to pull live from ERP.
              When done, click <b>Finalise &amp; Freeze Again</b> to mint v{nextVer}.
              {data?.settlement_unlock_reason && (
                <div className="mt-1">
                  <b>Reason:</b> {data.settlement_unlock_reason}
                  {data.settlement_unlocked_by && <> · by <b>{data.settlement_unlocked_by}</b></>}
                </div>
              )}
            </div>
          </div>
        )}
        {loading || !snap ? (
          <div className="py-12 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Computing…</div>
        ) : (
          <div className="space-y-4">
            {/* Sales card */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-slate-700">Sales · {snap.period?.from} → {snap.period?.to}</div>
                <div className="text-[11px] text-slate-400">Source: daily_sales</div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                <Tile label="Cash Sales" value={inr(sales.total_cash_sale)} />
                <Tile label="Card" value={inr(sales.card_idfc)} />
                <Tile label="UPI" value={inr(sales.bharat_pay)} />
                <Tile label="Aggregator" value={inr(sales.aggregator_total)} />
                <Tile label="Other" value={inr(sales.online_other)} />
                <Tile label="Refunds / Due" value={inr(sales.due_amount)} />
                <Tile label="GST" value={inr(sales.gst_amount)} />
                <Tile label="Gross Operational Sales" value={inr(sales.total_sale)} accent="green" />
              </div>
            </div>

            {/* Expenses card */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="font-medium text-slate-700">Operating Expenses ({inr(c.operating_expenses_total)})</div>
                <Button size="sm" variant="outline" onClick={loadDrill} data-testid="settle-expense-drill">Show line items</Button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {(snap.expenses_by_head || []).map(h => (
                  <div key={h.expense_head} className="border rounded px-2 py-1">
                    <div className="text-[10px] text-slate-400 uppercase">{h.expense_head}</div>
                    <div className="font-semibold text-red-700">{inr(h.amount)}</div>
                    <div className="text-[10px] text-slate-400">{h.count} entries · {h.pct_of_total}%</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Credits + Liabilities — editable in both LIVE and REOPENED states, hidden only when fully FROZEN. */}
            {!isReadOnly && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <ListEditor
                title="Other Approved Credits"
                rows={snap.credits || []}
                draft={addingCredit} setDraft={setAddingCredit}
                onAdd={addCredit}
                onDelete={(rid) => deleteLiab(rid, 'credits')}
                accent="green"
                includeDoubleDip={false}
                testPrefix="settle-credit"
              />
              <ListEditor
                title="Additional Settlement Liabilities"
                rows={snap.additional_liabilities || []}
                draft={addingLiab} setDraft={setAddingLiab}
                onAdd={addLiab}
                onDelete={(rid) => deleteLiab(rid, 'liabilities')}
                accent="red"
                includeDoubleDip
                testPrefix="settle-liab"
              />
            </div>)}

            {/* Final formula */}
            <div className="border-2 border-emerald-200 bg-emerald-50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <Row label={`Working Capital (Opening ${c.opening_wc_month ? '· ' + c.opening_wc_month : ''})`} value={inr(c.working_capital_balance)} src={c.working_capital_source || snap.working_capital?.source} />
                <Row label="+ Gross Operational Sales" value={inr(c.gross_operational_sales)} src={`daily_sales (${snap.period?.from} → ${snap.period?.to})`} />
                <Row label="+ Other Approved Credits" value={inr(c.other_approved_credits)} src="per-exit credits" />
                <Row label="− Operating Expenses" value={inr(c.operating_expenses_total)} src="db.expenses (Sales Dashboard)" negative />
                <Row label="− Additional Liabilities" value={inr(c.additional_settlement_liabilities)} src="per-exit liabilities (non-duplicated)" negative />
                {c.excluded_double_dip > 0 && (
                  <div className="col-span-2 text-[11px] text-amber-700 italic">
                    ⓘ {inr(c.excluded_double_dip)} was excluded because those liability rows are already inside Sales Dashboard expenses.
                  </div>
                )}
                <div className="col-span-2 text-[11px] text-slate-500 italic">
                  ✓ Opening WC = balance at start of exit month. Current-month sales & expenses are added as line items above so they are NOT counted twice.
                </div>
                <div className="col-span-2 border-t pt-2 flex items-center justify-between">
                  <span className="font-semibold text-lg">Final Settlement</span>
                  <span className={`font-bold text-xl ${c.final_settlement_amount >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {inr(Math.abs(c.final_settlement_amount))}
                  </span>
                </div>
                <div className="col-span-2 text-sm text-slate-600 font-medium">{c.verdict}</div>
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <div className="flex-1 text-[10px] text-slate-400 italic">
            Every deduction shows its source. Double-dip prevented at the API layer.
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
          {/* iter288 — Frozen state: SA/MGT can Reopen to v2 draft */}
          {isFrozen && (
            <Button variant="outline" onClick={() => setShowReopen(true)}
              className="border-amber-400 text-amber-800 hover:bg-amber-50"
              data-testid="settle-reopen-btn">
              Reopen Settlement
            </Button>
          )}
          {/* iter288 — Reopened state: Preview is live (modal body); Save Draft
                is implicit (every CRUD persists); Refreeze mints v{next}. */}
          {isReopened && (
            <>
              <Button variant="outline" onClick={load}
                data-testid="settle-save-draft-btn"
                title="Reloads the live computed view with any corrections you have made.">
                Save Draft &amp; Preview
              </Button>
              <Button onClick={() => setShowRefreeze(true)}
                disabled={refreezing}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="settle-refreeze-btn">
                {refreezing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                Finalise &amp; Freeze Again (v{nextVer})
              </Button>
            </>
          )}
          {/* Live first-time finalise (unchanged) */}
          {!isFrozen && !isReopened && (
            <Button onClick={finalize} disabled={finalizing || loading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="settle-finalize">
              {finalizing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
              Finalize &amp; Freeze
            </Button>
          )}
        </DialogFooter>

        {/* iter288 — Reopen confirmation dialog */}
        <Dialog open={showReopen} onOpenChange={setShowReopen}>
          <DialogContent className="max-w-md" data-testid="settle-reopen-dialog">
            <DialogHeader>
              <DialogTitle>Reopen Financial Settlement v{frozenVer}?</DialogTitle>
              <DialogDescription>
                Reopening archives v{frozenVer} and lets you build v{nextVer}.
                v{frozenVer} (figures, PDF, audit log, signatures) is preserved
                and never deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">
                  Reason for Reopening *
                </label>
                <select
                  className="w-full border rounded h-8 px-2 text-sm mt-1"
                  value={reopenReason.split(':')[0] || ''}
                  onChange={(e) => setReopenReason(e.target.value)}
                  data-testid="settle-reopen-reason-select"
                >
                  <option value="">— Select —</option>
                  <option>Incorrect Working Capital</option>
                  <option>Missing liability</option>
                  <option>Incorrect salary amount</option>
                  <option>Rental correction</option>
                  <option>Vendor reconciliation</option>
                  <option>Utility correction</option>
                  <option>ERP source correction</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">
                  Remarks *
                </label>
                <Textarea
                  placeholder="Describe the correction being made (visible in audit log)"
                  rows={3}
                  value={reopenReason.includes(':') ? reopenReason.split(':').slice(1).join(':').trim() : ''}
                  onChange={(e) => {
                    const head = reopenReason.split(':')[0] || '';
                    setReopenReason(`${head}: ${e.target.value}`);
                  }}
                  data-testid="settle-reopen-remarks"
                />
              </div>
              <label className="flex items-start gap-2 text-xs text-slate-700">
                <input type="checkbox" className="mt-0.5"
                  checked={reopenAck}
                  onChange={(e) => setReopenAck(e.target.checked)}
                  data-testid="settle-reopen-ack" />
                <span>
                  I understand that reopening creates a new settlement revision
                  (v{nextVer}) and does not erase the previously frozen
                  settlement (v{frozenVer}).
                </span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReopen(false)}>Cancel</Button>
              <Button className="bg-amber-600 hover:bg-amber-700 text-white"
                onClick={doReopen}
                disabled={reopening
                            || !reopenAck
                            || !reopenReason.split(':')[0]?.trim()
                            || !(reopenReason.split(':').slice(1).join(':') || '').trim()}
                data-testid="settle-reopen-confirm">
                {reopening && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                Confirm Reopen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* iter288 — Refreeze confirmation dialog */}
        <Dialog open={showRefreeze} onOpenChange={setShowRefreeze}>
          <DialogContent className="max-w-md" data-testid="settle-refreeze-dialog">
            <DialogHeader>
              <DialogTitle>Finalise &amp; Freeze as v{nextVer}?</DialogTitle>
              <DialogDescription>
                This writes a new frozen snapshot (v{nextVer}) using the
                current corrections.  v{frozenVer} stays in history.  The
                Exit Certificate re-opens from PENDING to ELIGIBLE if the
                other sections are also frozen.
              </DialogDescription>
            </DialogHeader>
            <Textarea
              placeholder="Optional note on v{nextVer} (shown in audit log)"
              rows={2}
              value={refreezeReason}
              onChange={(e) => setRefreezeReason(e.target.value)}
              data-testid="settle-refreeze-reason"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRefreeze(false)}>Cancel</Button>
              <Button className="bg-emerald-600 hover:bg-emerald-700"
                onClick={doRefreeze} disabled={refreezing}
                data-testid="settle-refreeze-confirm">
                {refreezing && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
                Freeze v{nextVer}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Expense drill drawer */}
        {showDrill && drill && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center" onClick={() => setShowDrill(false)}>
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[80vh] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Expense Details — {drill.rows.length} entries</h3>
                <Button size="sm" variant="ghost" onClick={() => setShowDrill(false)}>Close</Button>
              </div>
              <Table>
                <TableHeader><TableRow>
                  <TableHead>Date</TableHead><TableHead>Head</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Ref</TableHead><TableHead>Mode</TableHead>
                  <TableHead className="text-[10px] text-slate-400">Source</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {drill.rows.map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{r.date}</TableCell>
                      <TableCell className="text-xs"><Badge variant="outline" className="text-[10px]">{r.expense_head}</Badge></TableCell>
                      <TableCell className="text-xs">{r.description}</TableCell>
                      <TableCell className="text-right font-semibold text-red-700">{inr(r.amount)}</TableCell>
                      <TableCell className="text-[11px]">{r.reference || '—'}</TableCell>
                      <TableCell className="text-[11px]">{r.payment_mode || '—'}</TableCell>
                      <TableCell className="text-[10px] text-slate-400">{r.source}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="mt-3 text-sm">
                <strong>Totals by head:</strong>
                <ul className="ml-4 mt-1">
                  {drill.by_head.map(b => <li key={b.expense_head}>{b.expense_head}: {inr(b.amount)} ({b.count})</li>)}
                </ul>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Tile({ label, value, accent }) {
  return (
    <div className={`border rounded px-2 py-1 ${accent === 'green' ? 'bg-emerald-50 border-emerald-200' : ''}`}>
      <div className="text-[10px] text-slate-400 uppercase">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}
function Row({ label, value, src, negative }) {
  return (
    <>
      <div className="text-slate-600">{label}<span className="ml-1 text-[10px] text-slate-400">{src && `(${src})`}</span></div>
      <div className={`text-right font-semibold ${negative ? 'text-red-700' : 'text-slate-800'}`}>{value}</div>
    </>
  );
}
function ListEditor({ title, rows, draft, setDraft, onAdd, onDelete, accent, includeDoubleDip, testPrefix }) {
  return (
    <div className={`border rounded-lg p-3 ${accent === 'red' ? 'bg-red-50/40' : 'bg-emerald-50/40'}`}>
      <div className="font-medium text-slate-700 mb-2">{title}</div>
      {rows.length === 0 && <p className="text-xs text-slate-400 italic mb-2">None yet.</p>}
      <div className="space-y-1 mb-2">
        {rows.map(r => (
          <div key={r.row_id} className="flex items-center gap-2 text-xs border rounded px-2 py-1 bg-white">
            <span className="font-medium min-w-16">{r.head}</span>
            <span className="flex-1 truncate">{r.description}</span>
            {r.already_in_expenses && <Badge variant="outline" className="text-[9px] px-1 py-0 bg-amber-100 text-amber-700 border-amber-200">already in expenses</Badge>}
            <span className="font-semibold">{inr(r.amount)}</span>
            <Button size="icon" variant="ghost" className="h-5 w-5 text-red-500" onClick={() => onDelete(r.row_id)}><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-12 gap-1 items-end">
        <Input className="col-span-3 h-7 text-xs" placeholder="head" value={draft.head} onChange={(e) => setDraft(p => ({ ...p, head: e.target.value.toUpperCase() }))} data-testid={`${testPrefix}-head`} />
        <Input className="col-span-5 h-7 text-xs" placeholder="description" value={draft.description} onChange={(e) => setDraft(p => ({ ...p, description: e.target.value }))} data-testid={`${testPrefix}-desc`} />
        <Input className="col-span-3 h-7 text-xs text-right" type="number" placeholder="amount" value={draft.amount} onChange={(e) => setDraft(p => ({ ...p, amount: e.target.value }))} data-testid={`${testPrefix}-amt`} />
        <Button size="sm" className="col-span-1 h-7 px-2" onClick={onAdd} data-testid={`${testPrefix}-add`}><Plus className="w-3 h-3" /></Button>
        {includeDoubleDip && (
          <label className="col-span-12 flex items-center gap-1 text-[10px] text-slate-600 -mt-1">
            <Checkbox checked={!!draft.already_in_expenses} onCheckedChange={(v) => setDraft(p => ({ ...p, already_in_expenses: !!v }))} data-testid={`${testPrefix}-dd`} />
            <span>Already inside Sales Dashboard expenses (exclude to avoid double-deduction)</span>
          </label>
        )}
      </div>
    </div>
  );
}

/* ── Closure checklists modal ─────────────────────────────────── */
const CHECKLISTS = [
  { key: 'lease',           title: 'Rental / Triparty Lease Exit', icon: '🏠', singleton: true },
  { key: 'debrand',         title: 'Brand De-Branding Checklist',  icon: '🎨' },
  { key: 'social',          title: 'Social Media Handover',        icon: '📱' },
  { key: 'digital',         title: 'Digital Presence Exit',        icon: '🌐' },
  { key: 'access',          title: 'Digital Access & Data Handover', icon: '🔐' },
  { key: 'confidentiality', title: 'Confidential Info Acknowledgement', icon: '📕' },
  { key: 'compliance',      title: 'Licence / Compliance Closure',  icon: '📜' },
  { key: 'open_matters',    title: 'Open / Reserved Matters',      icon: '⚠️' },
];

export function ExitChecklistsPanel({ open, onOpenChange, exitId, token, isSA = false, isAccounts = false, onSectionsChanged }) {
  const [expanded, setExpanded] = useState({ lease: true });
  const [dataMap, setDataMap] = useState({});
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(false);
  const [freezeMap, setFreezeMap] = useState({});          // section -> freeze row
  const [showFreezeAll, setShowFreezeAll] = useState(false);
  const [freezeAllData, setFreezeAllData] = useState(null); // dry-run result
  const [showForceFreeze, setShowForceFreeze] = useState(null);  // section key when opening SA-force
  const [forceReason, setForceReason] = useState('');
  const [showReopen, setShowReopen] = useState(null);
  const [reopenReason, setReopenReason] = useState('');
  const [freezing, setFreezing] = useState(false);

  const loadSection = useCallback(async (key) => {
    try { const r = await api(`/checklist/${key}/list/${exitId}`, token); setDataMap(p => ({ ...p, [key]: r })); }
    catch (e) { toast.error(`${key}: ${e.message}`); }
  }, [exitId, token]);

  const loadFreezeState = useCallback(async () => {
    try {
      const r = await api(`/section/freeze-all-ready/${exitId}`, token, { dry_run: true });
      const map = {};
      (r.already_frozen || []).forEach(x => { map[x.section] = { status: 'FROZEN', ...x }; });
      setFreezeMap(map);
      setFreezeAllData(r);
    } catch (e) { /* silent */ }
  }, [exitId, token]);

  const seedSection = async (key) => {
    setLoading(true);
    try { await api(`/checklist/${key}/seed/${exitId}`, token); await loadSection(key); toast.success(`${key} seeded`); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open) return;
    CHECKLISTS.forEach(c => loadSection(c.key));
    loadFreezeState();
  }, [open, loadSection, loadFreezeState]);

  const markAllReviewed = async (key) => {
    if (!window.confirm('Mark all PENDING rows in this section as complete? Rows already carrying a completion status stay untouched.')) return;
    try {
      const r = await fetch(`${API}/api/franchise-exit/checklist/${key}/mark-all-reviewed/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, only_pending: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Bulk mark failed');
      toast.success(`Updated ${d.updated} row(s) → ${d.status}`);
      await loadSection(key);
      await loadFreezeState();
    } catch (e) { toast.error(e.message); }
  };

  const upsertRow = async (key, rowId, patch) => {
    try { await api(`/checklist/${key}/upsert-row/${exitId}`, token, { row_id: rowId, patch }); await loadSection(key); }
    catch (e) { toast.error(e.message); }
  };
  const addRow = async (key) => {
    const d = drafts[key] || {};
    if (!d.item) { toast.error('item required'); return; }
    try { await api(`/checklist/${key}/upsert-row/${exitId}`, token, { patch: d }); setDrafts(p => ({ ...p, [key]: {} })); await loadSection(key); }
    catch (e) { toast.error(e.message); }
  };
  const delRow = async (key, rid) => {
    if (!window.confirm('Remove?')) return;
    try { await api(`/checklist/${key}/delete-row/${exitId}`, token, { row_id: rid }); await loadSection(key); }
    catch (e) { toast.error(e.message); }
  };

  const doFreeze = async (section, force = false, reason = '') => {
    setFreezing(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/section/freeze/${exitId}/${section}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason, force }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Freeze failed');
      toast.success(`${section} frozen (v${d.freeze.version})`);
      setShowForceFreeze(null); setForceReason('');
      await loadFreezeState();
      if (onSectionsChanged) onSectionsChanged();
    } catch (e) { toast.error(e.message); }
    finally { setFreezing(false); }
  };

  const doReopen = async (section, reason) => {
    if (!reason.trim()) { toast.error('Reason mandatory'); return; }
    setFreezing(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/section/reopen/${exitId}/${section}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason: reason.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Reopen failed');
      toast.success(`${section} reopened (v${d.version_superseded} superseded)`);
      setShowReopen(null); setReopenReason('');
      await loadFreezeState();
      if (onSectionsChanged) onSectionsChanged();
    } catch (e) { toast.error(e.message); }
    finally { setFreezing(false); }
  };

  const doFreezeAll = async () => {
    setFreezing(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/section/freeze-all-ready/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, dry_run: false }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Bulk freeze failed');
      toast.success(`${(d.frozen_now || []).length} section(s) frozen`);
      setShowFreezeAll(false);
      await loadFreezeState();
      if (onSectionsChanged) onSectionsChanged();
    } catch (e) { toast.error(e.message); }
    finally { setFreezing(false); }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="exit-checklists-modal">
        <DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <DialogTitle>Exit Closure Checklists</DialogTitle>
              <DialogDescription>Track every closure obligation. Once every mandatory row is DONE, Finalise & Freeze the section to lock its authoritative status.</DialogDescription>
            </div>
            {isSA && freezeAllData && (
              <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setShowFreezeAll(true)} data-testid="btn-freeze-all-completed">
                <Lock className="w-3 h-3 mr-1" /> Freeze All Completed ({(freezeAllData.ready || []).length})
              </Button>
            )}
          </div>
        </DialogHeader>
        <div className="space-y-2">
          {CHECKLISTS.map(sec => {
            const rows = dataMap[sec.key];
            const freeze = freezeMap[sec.key];
            const isFrozen = freeze?.status === 'FROZEN';
            const isOpen = !!expanded[sec.key];
            const allDone = rows && !sec.singleton && (rows.count > 0) && (rows.completed === rows.count);
            const singletonReady = sec.singleton && rows?.rag && (rows.rag.effective === 'GREEN' || rows.rag.effective === 'GREEN_OVERRIDE');
            const canFreeze = !isFrozen && (allDone || singletonReady);
            return (
              <div key={sec.key} className={`border rounded-lg ${isFrozen ? 'border-emerald-300 bg-emerald-50/40' : ''}`}>
                <button type="button" onClick={() => setExpanded(p => ({ ...p, [sec.key]: !p[sec.key] }))}
                  className="w-full flex items-center gap-2 p-3 text-left" data-testid={`checklist-toggle-${sec.key}`}>
                  {isOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                  <span>{sec.icon}</span>
                  <span className="font-medium">{sec.title}</span>
                  {rows && !sec.singleton && (
                    <Badge variant="outline" className="ml-2 text-[11px]">
                      {rows.completed || 0} / {rows.count || 0} done
                    </Badge>
                  )}
                  {rows && sec.singleton && rows.row && !isFrozen && (
                    <Badge variant="outline" className="ml-2 text-[11px]">{rows.rag?.effective || 'seeded'}</Badge>
                  )}
                  {isFrozen && (
                    <Badge className="ml-2 text-[11px] bg-emerald-600 text-white border-emerald-700" data-testid={`checklist-frozen-badge-${sec.key}`}>
                      ✓ FROZEN v{freeze.version}
                    </Badge>
                  )}
                </button>
                {isOpen && (
                  <div className="border-t px-3 py-3">
                    {sec.singleton ? (
                      <LeasePanel row={(rows?.row?.payload) || {}} rag={rows?.rag} exitId={exitId} token={token} isSA={isSA} onSave={(patch) => upsertRow(sec.key, null, patch)} onReload={() => loadSection(sec.key)} />
                    ) : (
                      <>
                        {(!rows || rows.count === 0) && (
                          <Button size="sm" variant="outline" onClick={() => seedSection(sec.key)} disabled={loading} data-testid={`checklist-seed-${sec.key}`}>
                            <RefreshCw className="w-3 h-3 mr-1" /> Seed defaults
                          </Button>
                        )}
                        {rows && rows.count > 0 && (
                          <ChecklistTable section={sec.key} rows={rows.rows} onEdit={(rid, patch) => !isFrozen && upsertRow(sec.key, rid, patch)} onDelete={(rid) => !isFrozen && delRow(sec.key, rid)} readOnly={isFrozen} />
                        )}
                        {!isFrozen && (isSA || isAccounts) && rows && rows.count > 0 && rows.completed < rows.count && !sec.singleton && (
                          <div className="mt-2">
                            <Button size="sm" variant="outline" className="text-emerald-800 border-emerald-500" onClick={() => markAllReviewed(sec.key)} data-testid={`btn-mark-all-reviewed-${sec.key}`}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Mark All Reviewed as Complete
                            </Button>
                          </div>
                        )}
                        {!isFrozen && (
                          <div className="flex items-center gap-1 mt-2">
                            <Input className="h-7 text-xs" placeholder="+ Add custom item" value={drafts[sec.key]?.item || ''} onChange={(e) => setDrafts(p => ({ ...p, [sec.key]: { ...(p[sec.key] || {}), item: e.target.value } }))} data-testid={`checklist-add-${sec.key}`} />
                            <Button size="sm" onClick={() => addRow(sec.key)}><Plus className="w-3 h-3" /></Button>
                          </div>
                        )}
                      </>
                    )}
                    {/* Sticky footer — Finalise & Freeze / Reopen */}
                    <div className={`sticky bottom-0 -mx-3 -mb-3 mt-3 p-2 border-t flex items-center justify-between gap-2 ${isFrozen ? 'bg-emerald-50' : 'bg-slate-50'}`} data-testid={`checklist-footer-${sec.key}`}>
                      {isFrozen ? (
                        <>
                          <div className="text-[11px] text-emerald-800">
                            <b>✓ FROZEN</b> · v{freeze.version} · Frozen by <b>{freeze.frozen_by}</b> on <b>{String(freeze.frozen_at || '').slice(0, 16).replace('T', ' ')}</b>
                          </div>
                          {isSA && (
                            <Button size="sm" variant="outline" className="text-amber-800 border-amber-400" onClick={() => { setShowReopen(sec.key); setReopenReason(''); }} data-testid={`btn-reopen-section-${sec.key}`}>
                              <Unlock className="w-3 h-3 mr-1" /> Reopen Section
                            </Button>
                          )}
                        </>
                      ) : (
                        <>
                          <div className="text-[11px] text-slate-600">
                            {sec.singleton
                              ? `RAG: ${rows?.rag?.effective || '—'}`
                              : `Completed ${rows?.completed || 0} / ${rows?.count || 0}`}
                          </div>
                          <div className="flex items-center gap-2">
                            {!canFreeze && isSA && (rows?.count > 0 || sec.singleton) && (
                              <Button size="sm" variant="outline" className="text-amber-800 border-amber-500" onClick={() => { setShowForceFreeze(sec.key); setForceReason(''); }} data-testid={`btn-force-freeze-${sec.key}`}>
                                Force Freeze (SA)
                              </Button>
                            )}
                            {(isSA || isAccounts) && (
                              <Button size="sm" onClick={() => doFreeze(sec.key)} disabled={!canFreeze || freezing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`btn-freeze-${sec.key}`}>
                                <Lock className="w-3 h-3 mr-1" /> Finalise & Freeze
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>

      {/* Force freeze dialog */}
      <Dialog open={!!showForceFreeze} onOpenChange={(v) => !v && setShowForceFreeze(null)}>
        <DialogContent className="max-w-md" data-testid="force-freeze-modal">
          <DialogHeader>
            <DialogTitle>Force Freeze — Super Admin</DialogTitle>
            <DialogDescription className="text-xs">
              Some rows are still PENDING. Force-freezing accepts the section as complete for audit purposes — a mandatory reason is stored.
            </DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Mandatory reason" value={forceReason} onChange={(e) => setForceReason(e.target.value)} rows={3} data-testid="input-force-freeze-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForceFreeze(null)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => { if (forceReason.trim()) doFreeze(showForceFreeze, true, forceReason.trim()); else toast.error('Reason mandatory'); }} disabled={freezing} data-testid="btn-force-freeze-confirm">
              Force Freeze
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen dialog */}
      <Dialog open={!!showReopen} onOpenChange={(v) => !v && setShowReopen(null)}>
        <DialogContent className="max-w-md" data-testid="reopen-section-modal">
          <DialogHeader>
            <DialogTitle>Reopen Section — Super Admin</DialogTitle>
            <DialogDescription className="text-xs">
              Reopening flips the current frozen version to SUPERSEDED_PENDING_REFREEZE. The prior version is preserved in history — never destroyed. After correction, Finalise & Freeze creates a new version.
            </DialogDescription>
          </DialogHeader>
          <Textarea placeholder="Mandatory reason for reopening" value={reopenReason} onChange={(e) => setReopenReason(e.target.value)} rows={3} data-testid="input-reopen-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReopen(null)}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => doReopen(showReopen, reopenReason)} disabled={freezing} data-testid="btn-reopen-confirm">
              Reopen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Freeze all ready dialog */}
      <Dialog open={showFreezeAll} onOpenChange={setShowFreezeAll}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="freeze-all-modal">
          <DialogHeader>
            <DialogTitle>Freeze All Completed Sections</DialogTitle>
            <DialogDescription className="text-xs">
              Locks every ready section as FROZEN in one click. Sections that are still incomplete are shown but not touched.
            </DialogDescription>
          </DialogHeader>
          {freezeAllData && (
            <div className="space-y-2 text-sm">
              <div className="border rounded p-2">
                <b className="text-emerald-700">READY TO FREEZE ({(freezeAllData.ready || []).length}):</b>
                <ul className="mt-1 space-y-0.5">
                  {(freezeAllData.ready || []).map(r => (
                    <li key={r.section} className="text-xs">✓ {r.label} — {r.done}/{r.total}{r.override ? ' (SA Override)' : ''}{r.nil_declared ? ' (NIL)' : ''}</li>
                  ))}
                  {(freezeAllData.ready || []).length === 0 && <li className="text-xs text-slate-500">Nothing to freeze right now.</li>}
                </ul>
              </div>
              {(freezeAllData.already_frozen || []).length > 0 && (
                <div className="border rounded p-2 bg-slate-50">
                  <b className="text-slate-700">ALREADY FROZEN ({(freezeAllData.already_frozen || []).length}):</b>
                  <ul className="mt-1 space-y-0.5">
                    {freezeAllData.already_frozen.map(r => (
                      <li key={r.section} className="text-xs">✓ {r.label} — v{r.version}</li>
                    ))}
                  </ul>
                </div>
              )}
              {(freezeAllData.not_ready || []).length > 0 && (
                <div className="border rounded p-2 bg-amber-50">
                  <b className="text-amber-700">NOT READY ({(freezeAllData.not_ready || []).length}):</b>
                  <ul className="mt-1 space-y-0.5">
                    {freezeAllData.not_ready.map(r => (
                      <li key={r.section} className="text-xs">✗ {r.label} — {r.done}/{r.total}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFreezeAll(false)}>Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={doFreezeAll} disabled={freezing || !(freezeAllData?.ready?.length)} data-testid="btn-confirm-freeze-all">
              Freeze All Ready Sections
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

const CHECKLISTS_ = [];  // suppress lint noise

function ChecklistTable({ section, rows, onEdit, onDelete, readOnly = false }) {
  return (
    <Table>
      <TableHeader><TableRow>
        <TableHead>Item</TableHead>
        <TableHead>Status</TableHead>
        <TableHead>Remarks</TableHead>
        <TableHead className="w-8" />
      </TableRow></TableHeader>
      <TableBody>
        {rows.map(r => {
          // FR-007 P0 (iter233) — social rows store their state on
          // ``access_status`` (same field as the "access" checklist);
          // the ChecklistTable used to write to the wrong ``status``
          // field which is why every social row rendered PENDING in
          // the Annexure G PDF even when remarks confirmed closure.
          const statusField = (section === 'access' || section === 'social')
                                ? 'access_status'
                                : section === 'confidentiality' ? 'return_delete_status'
                                : 'status';
          const options = section === 'access'
            ? ['PENDING', 'REMOVED', 'DEACTIVATED', 'TRANSFERRED', 'RETAINED', 'RETAINED_BY_MFPL', 'NOT_APPLICABLE', 'DONE']
            : section === 'social'
              // Full completion vocabulary — every non-PENDING value
              // counts as complete for exit purposes.
              ? ['PENDING', 'DEACTIVATED', 'REMOVED', 'TRANSFERRED', 'CLOSED', 'RETAINED_BY_MFPL', 'NOT_APPLICABLE', 'DONE']
              : section === 'confidentiality'
                ? ['PENDING', 'RETURNED', 'DELETED', 'RETAINED']
                : section === 'digital'
                  ? ['PENDING', 'KEEP', 'TRANSFER', 'DEACTIVATED', 'REMOVED', 'RENAMED', 'CLOSED', 'NOT_APPLICABLE', 'DONE']
                  : section === 'compliance'
                    ? ['PENDING', 'CLOSE', 'TRANSFER', 'AMEND', 'RETAIN', 'NOT_APPLICABLE', 'DONE']
                    : ['PENDING', 'DONE', 'NOT_APPLICABLE'];
          return (
            <TableRow key={r.row_id} data-testid={`checklist-row-${r.row_id}`}>
              <TableCell className="font-medium text-xs">{r.item}</TableCell>
              <TableCell>
                <Select value={r[statusField] || options[0]} onValueChange={(v) => onEdit(r.row_id, { [statusField]: v })} disabled={readOnly}>
                  <SelectTrigger className="h-7 text-xs" data-testid={`checklist-status-${r.row_id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>{options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Input defaultValue={r.remarks || ''} className="h-7 text-xs" readOnly={readOnly} onBlur={(e) => !readOnly && e.target.value !== (r.remarks || '') && onEdit(r.row_id, { remarks: e.target.value })} />
              </TableCell>
              <TableCell>{!readOnly && <Button size="icon" variant="ghost" className="h-6 w-6 text-red-500" onClick={() => onDelete(r.row_id)}><Trash2 className="w-3 h-3" /></Button>}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function LeaseField({ k, lbl, options, type = 'text', wide = false, f, setV }) {
  if (options) {
    return (
      <div className={wide ? 'md:col-span-2' : ''}>
        <label className="text-[11px] text-slate-500 block">{lbl}</label>
        <Select value={f[k] || undefined} onValueChange={(v) => setV(k, v === '__clear__' ? '' : v)}>
          <SelectTrigger className="h-7 text-xs" data-testid={`lease-${k}`}><SelectValue placeholder="—" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__clear__">—</SelectItem>
            {options.filter(o => o).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }
  return (
    <div className={wide ? 'md:col-span-2' : ''}>
      <label className="text-[11px] text-slate-500 block">{lbl}</label>
      <Input type={type} value={f[k] ?? ''} onChange={(e) => setV(k, type === 'number' ? Number(e.target.value || 0) : e.target.value)} className="h-7 text-xs" data-testid={`lease-${k}`} />
    </div>
  );
}

/* ── Annexure E — Rental / Tripartite Lease Exit ─────────────── */
const YN = ['', 'YES', 'NO', 'N.A.'];
const YNP = ['', 'YES', 'NO', 'PENDING'];
const DOC_STATUS = ['', 'Uploaded', 'Pending', 'N.A.'];
const PAY_STATUS = ['', 'PENDING', 'PART PAID', 'PAID', 'DISPUTED'];
const RAG_COLORS = {
  GREEN: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  GREEN_OVERRIDE: 'bg-emerald-100 text-emerald-900 border-emerald-500',
  AMBER: 'bg-amber-100 text-amber-800 border-amber-300',
  RED: 'bg-red-100 text-red-800 border-red-300',
};
const FORCE_GREEN_REASON_CODES = [
  { code: 'LANDLORD_DISCUSSION_COMPLETED',    label: 'Landlord discussion completed' },
  { code: 'FRANCHISEE_ACCEPTS_RESPONSIBILITY', label: 'Franchisee accepts responsibility' },
  { code: 'MFPL_LIABILITY_RELEASED',           label: 'MFPL liability released/accepted' },
  { code: 'LEGAL_MATTER_DOCUMENTED_SEPARATELY', label: 'Legal matter documented separately' },
  { code: 'TIME_BOUND_CLOSURE_ACCEPTED',       label: 'Time-bound closure accepted' },
  { code: 'MANAGEMENT_APPROVED_EXCEPTION',     label: 'Management-approved exception' },
  { code: 'OTHER',                              label: 'Other' },
];

function LeasePanel({ row, rag, exitId, token, isSA = false, onSave, onReload }) {
  const [f, setF] = useState({});
  const [dirty, setDirty] = useState(false);
  // iter270 — Save/Draft state machine. Values: idle | saving | saved | failed.
  const [saveState, setSaveState] = useState('idle');
  const [saveError, setSaveError] = useState('');
  const [lastSavedAt, setLastSavedAt] = useState(null);
  // Track which keys the user has explicitly edited this session. Fresh
  // server values for these keys must NOT overwrite in-progress edits;
  // untouched keys are freely refreshed from the backend so background
  // reloads (RAG recalc, freeze, section-status updates) can update
  // computed values without clobbering user typing.
  const dirtyKeysRef = useRef(new Set());
  const bootedRef = useRef(false);
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overrideVal, setOverrideVal] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [forceGreenOpen, setForceGreenOpen] = useState(false);
  const [fgReasonCode, setFgReasonCode] = useState('');
  const [fgRemarks, setFgRemarks] = useState('');
  const [fgConfirmed, setFgConfirmed] = useState(false);
  const [fgSubmitting, setFgSubmitting] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);

  // iter270 — MERGE incoming row instead of REPLACING.
  //   • On first mount → seed form fully from the backend row.
  //   • On subsequent parent refreshes → keep every key the user has
  //     touched (`dirtyKeysRef`) and update only the untouched ones.
  //   • NEVER overwrite the form with an empty/absent value if the user
  //     has any pending unsaved edits.  A stale/partial refresh cannot
  //     wipe typed text.
  useEffect(() => {
    const incoming = row || {};
    if (!bootedRef.current) {
      setF(incoming);
      bootedRef.current = true;
      return;
    }
    setF(prev => {
      const merged = { ...prev };
      const touched = dirtyKeysRef.current;
      for (const [k, v] of Object.entries(incoming)) {
        if (touched.has(k)) continue;           // preserve pending edit
        merged[k] = v;
      }
      return merged;
    });
    // eslint-disable-next-line
  }, [row]);

  const setV = (k, v) => {
    dirtyKeysRef.current.add(k);
    setF(p => ({ ...p, [k]: v }));
    setDirty(true);
    setSaveState('idle');   // reset badge once user starts editing again
  };
  const eff = (rag && rag.effective) || 'RED';
  const isOverride = eff === 'GREEN_OVERRIDE';
  const badge = RAG_COLORS[eff] || RAG_COLORS.RED;
  const badgeText = isOverride ? 'GREEN — SA OVERRIDE' : eff;
  const fg = (rag && rag.force_green) || null;

  const save = async () => {
    setSaving(true);
    setSaveState('saving');
    setSaveError('');
    try {
      // Only send the keys the user actually changed — protects against
      // partial payloads accidentally clearing unrelated fields on the
      // backend if the reducer ever changes to a naive $set.
      const patch = {};
      dirtyKeysRef.current.forEach(k => { patch[k] = f[k] ?? ''; });
      const payload = Object.keys(patch).length ? patch : f;
      await onSave(payload);
      dirtyKeysRef.current.clear();
      setDirty(false);
      setSaveState('saved');
      setLastSavedAt(new Date());
      toast.success('Annexure E saved');
    }
    catch (e) {
      setSaveState('failed');
      setSaveError(e.message || 'Save failed');
      toast.error(e.message || 'Save failed');
    }
    finally { setSaving(false); }
  };

  // iter270 — warn before leaving / closing when there are unsaved edits.
  useEffect(() => {
    const handler = (e) => {
      if (!dirty) return undefined;
      e.preventDefault();
      e.returnValue = '';   // Chrome requires returnValue set
      return '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const submitOverride = async () => {
    if (!overrideReason.trim()) { toast.error('Reason required'); return; }
    try {
      const r = await fetch(`${API}/api/franchise-exit/lease/rag-override/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, rag: overrideVal || null, reason: overrideReason }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'override failed');
      toast.success('RAG override applied');
      setOverrideOpen(false); setOverrideReason(''); setOverrideVal('');
      onReload && onReload();
    } catch (e) { toast.error(e.message); }
  };

  const resetForceGreenForm = () => {
    setFgReasonCode(''); setFgRemarks(''); setFgConfirmed(false);
  };

  const submitForceGreen = async () => {
    if (!fgReasonCode) { toast.error('Please pick an override reason'); return; }
    if (fgReasonCode === 'OTHER' && !fgRemarks.trim()) {
      toast.error('Remarks are mandatory when reason is Other'); return;
    }
    if (!fgConfirmed) { toast.error('Please confirm the acknowledgement checkbox'); return; }
    setFgSubmitting(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/lease/rag-force-green/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason_code: fgReasonCode, remarks: fgRemarks.trim(), confirmed: true }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Force GREEN failed');
      toast.success('Annexure E forced to GREEN — SA OVERRIDE');
      setForceGreenOpen(false); resetForceGreenForm();
      onReload && onReload();
    } catch (e) { toast.error(e.message); }
    finally { setFgSubmitting(false); }
  };

  const submitRevoke = async () => {
    if (!revokeReason.trim()) { toast.error('Reason required to revoke override'); return; }
    setRevoking(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/lease/rag-force-green-revoke/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason: revokeReason.trim() }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Revoke failed');
      toast.success('Force GREEN override revoked');
      setRevokeOpen(false); setRevokeReason('');
      onReload && onReload();
    } catch (e) { toast.error(e.message); }
    finally { setRevoking(false); }
  };

  const utils = f.utility_accounts || [];
  const setUtilRow = (idx, patch) => {
    const next = utils.map((u, i) => i === idx ? { ...u, ...patch } : u);
    setV('utility_accounts', next);
  };
  const addUtil = () => setV('utility_accounts', [...(utils || []), { account_name: '', account_number: '', current_holder: '', final_bill: 0, transfer_closure: 'PENDING', status: 'PENDING' }]);
  const delUtil = (idx) => setV('utility_accounts', (utils || []).filter((_, i) => i !== idx));

  const timeline = f.timeline_items || [];
  const setTimelineRow = (idx, patch) => {
    const next = timeline.map((t, i) => i === idx ? { ...t, ...patch } : t);
    setV('timeline_items', next);
  };
  const addTimeline = (task) => setV('timeline_items', [...(timeline || []), { task, owner: '', due_date: '', status: 'PENDING', proof: '', verified_by: '' }]);
  const delTimeline = (idx) => setV('timeline_items', (timeline || []).filter((_, i) => i !== idx));

  const seedTimelines = () => {
    if (timeline.length) return;
    const defaults = [
      'Landlord meeting', 'Rent reconciliation', 'TDS/GST reconciliation',
      'Utility closure/transfer', 'MFPL release document',
      'Security deposit reconciliation', 'Signage/de-branding',
      'Keys/access', 'Landlord no-dues/release', 'Final premises closure',
    ];
    setV('timeline_items', defaults.map(task => ({ task, owner: '', due_date: '', status: 'PENDING', proof: '', verified_by: '' })));
  };

  return (
    <div className="space-y-3" data-testid="annexure-e-panel">
      {/* iter270 — Save-status bar (Unsaved / Saving / Saved / Failed). */}
      <div
        className={`sticky top-0 z-10 flex items-center justify-between px-3 py-2 rounded border text-xs ${
          saveState === 'saving' ? 'bg-blue-50 border-blue-300 text-blue-900' :
          saveState === 'saved'  ? 'bg-emerald-50 border-emerald-300 text-emerald-900' :
          saveState === 'failed' ? 'bg-red-50 border-red-300 text-red-900' :
          dirty                    ? 'bg-amber-50 border-amber-300 text-amber-900' :
                                     'bg-slate-50 border-slate-200 text-slate-700'
        }`}
        data-testid="lease-save-status-bar"
      >
        <div className="flex items-center gap-2">
          {saveState === 'saving' && <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>}
          {saveState === 'saved' && !dirty && (
            <><CheckCircle className="w-3 h-3" /> Saved successfully{lastSavedAt && ` · ${lastSavedAt.toLocaleTimeString()}`}</>
          )}
          {saveState === 'failed' && (
            <><AlertTriangle className="w-3 h-3" /> Save failed{saveError ? ` — ${saveError}` : ''}</>
          )}
          {saveState === 'idle' && dirty && (
            <><AlertTriangle className="w-3 h-3" /> Unsaved changes — click <strong>Save Draft</strong> to persist.</>
          )}
          {saveState === 'idle' && !dirty && (
            <>All Annexure E fields in sync with server.</>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant={dirty ? 'default' : 'outline'}
            onClick={save}
            disabled={saving || !dirty}
            data-testid="lease-save-draft-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
            Save Draft
          </Button>
          {dirty && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                if (!window.confirm('Discard your unsaved changes and reload from the server?')) return;
                dirtyKeysRef.current.clear();
                setDirty(false);
                setSaveState('idle');
                onReload && onReload();
              }}
              data-testid="lease-discard-btn"
            >
              Discard
            </Button>
          )}
        </div>
      </div>

      {/* RAG banner */}
      <div className="flex items-center justify-between p-2 bg-slate-50 rounded border">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-600">Annexure E Status:</span>
          <span className={`px-2 py-0.5 rounded-full border text-xs font-bold ${badge}`} data-testid="lease-rag-badge">{badgeText}</span>
          {isOverride && (
            <Badge variant="outline" className="text-[10px] border-emerald-400 text-emerald-800 bg-emerald-50" data-testid="lease-force-green-tag">
              Overridden by Super Admin
            </Badge>
          )}
          {rag?.manual_override && !isOverride && <Badge variant="outline" className="text-[10px]">SA Override active</Badge>}
          {rag?.auto && (rag?.manual_override || isOverride) && <span className="text-[10px] text-slate-500">(auto: {rag.auto})</span>}
        </div>
        <div className="flex items-center gap-2">
          {isSA && !isOverride && (
            <Button size="sm" variant="outline" onClick={() => setOverrideOpen(v => !v)} data-testid="lease-override-toggle">
              {overrideOpen ? 'Cancel Override' : 'SA Override RAG'}
            </Button>
          )}
          {isSA && (eff === 'AMBER' || eff === 'RED') && (
            <Button size="sm" variant="default" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => { resetForceGreenForm(); setForceGreenOpen(true); }} data-testid="lease-force-green-btn">
              <CheckCircle className="w-3 h-3 mr-1" /> Force GREEN / Override Complete
            </Button>
          )}
          {isSA && isOverride && (
            <Button size="sm" variant="outline" className="border-amber-500 text-amber-800" onClick={() => setRevokeOpen(true)} data-testid="lease-revoke-force-green">
              Revoke Override
            </Button>
          )}
        </div>
      </div>

      {/* Force GREEN meta strip */}
      {isOverride && fg && (
        <div className="p-2 border border-emerald-300 rounded bg-emerald-50 text-[11px] text-emerald-900 flex flex-wrap gap-x-4 gap-y-1" data-testid="lease-force-green-meta">
          <span><b>By:</b> {fg.by || '—'}</span>
          <span><b>Role:</b> {fg.role || 'SA'}</span>
          <span><b>At:</b> {(fg.at || '').replace('T', ' ').slice(0, 19) || '—'}</span>
          <span><b>Reason:</b> {fg.reason_label || fg.reason_code || '—'}</span>
          {fg.remarks && <span><b>Remarks:</b> {fg.remarks}</span>}
        </div>
      )}

      {overrideOpen && !isOverride && (
        <div className="p-2 border border-amber-300 rounded bg-amber-50 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <div>
              <label className="text-[11px] text-slate-500 block">Override to</label>
              <Select value={overrideVal || undefined} onValueChange={(v) => setOverrideVal(v === '__clear__' ? '' : v)}>
                <SelectTrigger className="h-7 text-xs" data-testid="lease-override-value"><SelectValue placeholder="— clear override —" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__clear__">— clear override —</SelectItem>
                  <SelectItem value="GREEN">GREEN</SelectItem>
                  <SelectItem value="AMBER">AMBER</SelectItem>
                  <SelectItem value="RED">RED</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <label className="text-[11px] text-slate-500 block">Reason (mandatory, audit-logged)</label>
              <Input value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} className="h-7 text-xs" data-testid="lease-override-reason" />
            </div>
          </div>
          <Button size="sm" onClick={submitOverride} data-testid="lease-override-submit"><CheckCircle className="w-3 h-3 mr-1" /> Apply Override</Button>
        </div>
      )}

      {/* Force GREEN modal */}
      <Dialog open={forceGreenOpen} onOpenChange={(v) => { setForceGreenOpen(v); if (!v) resetForceGreenForm(); }}>
        <DialogContent className="max-w-lg" data-testid="lease-force-green-modal">
          <DialogHeader>
            <DialogTitle className="text-red-700">FORCE ANNEXURE E TO GREEN</DialogTitle>
            <DialogDescription className="text-xs text-slate-700">
              You are overriding the system RAG status for a legal / rental exit section.
              Any unresolved lease / landlord / tripartite matter will remain recorded in the audit trail and Annexure K.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-slate-500 block">Override Reason <span className="text-red-500">*</span></label>
              <Select value={fgReasonCode || undefined} onValueChange={setFgReasonCode}>
                <SelectTrigger className="h-8 text-xs" data-testid="lease-force-green-reason"><SelectValue placeholder="Select a reason" /></SelectTrigger>
                <SelectContent>
                  {FORCE_GREEN_REASON_CODES.map(r => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] text-slate-500 block">
                Remarks {fgReasonCode === 'OTHER' && <span className="text-red-500">* (mandatory when reason is Other)</span>}
              </label>
              <Textarea value={fgRemarks} onChange={(e) => setFgRemarks(e.target.value)} rows={3} className="text-xs" data-testid="lease-force-green-remarks" />
            </div>
            <label className="flex items-start gap-2 text-xs text-slate-700">
              <Checkbox checked={fgConfirmed} onCheckedChange={(v) => setFgConfirmed(!!v)} data-testid="lease-force-green-confirm" />
              <span>I confirm that the available rental / lease information has been reviewed.</span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setForceGreenOpen(false); resetForceGreenForm(); }} data-testid="lease-force-green-cancel">Cancel</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={submitForceGreen} disabled={fgSubmitting} data-testid="lease-force-green-submit">
              {fgSubmitting ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
              Force GREEN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke override modal */}
      <Dialog open={revokeOpen} onOpenChange={(v) => { setRevokeOpen(v); if (!v) setRevokeReason(''); }}>
        <DialogContent className="max-w-md" data-testid="lease-revoke-modal">
          <DialogHeader>
            <DialogTitle>Revoke Force GREEN Override</DialogTitle>
            <DialogDescription className="text-xs text-slate-700">
              Reverting will recalculate Annexure E from the underlying checklist state.  This action is audit-logged.
            </DialogDescription>
          </DialogHeader>
          <div>
            <label className="text-[11px] text-slate-500 block">Reason (mandatory)</label>
            <Textarea value={revokeReason} onChange={(e) => setRevokeReason(e.target.value)} rows={3} className="text-xs" data-testid="lease-revoke-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRevokeOpen(false); setRevokeReason(''); }}>Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={submitRevoke} disabled={revoking} data-testid="lease-revoke-submit">
              {revoking ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
              Revoke Override
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* A. Property & agreement */}
      <Section title="A. Property & Agreement Details">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <LeaseField f={f} setV={setV} k="property_address" lbl="Property Address" wide />
          <LeaseField f={f} setV={setV} k="landlord" lbl="Landlord / Property Owner" />
          <LeaseField f={f} setV={setV} k="franchisee_entity" lbl="Franchisee / Occupying Entity" />
          <LeaseField f={f} setV={setV} k="original_agreement_date" lbl="Original Agreement Date" type="date" />
          <LeaseField f={f} setV={setV} k="agreement_type" lbl="Type of Agreement" options={['', 'Tripartite', 'Lease', 'Leave & Licence', 'Other']} />
          <LeaseField f={f} setV={setV} k="agreement_commencement_date" lbl="Commencement Date" type="date" />
          <LeaseField f={f} setV={setV} k="original_expiry_date" lbl="Original Expiry Date" type="date" />
          <LeaseField f={f} setV={setV} k="franchise_exit_effective_date" lbl="Franchise Exit Effective Date" type="date" />
          <LeaseField f={f} setV={setV} k="mfpl_release_effective_date" lbl="MFPL Release Effective Date" type="date" />
          <LeaseField f={f} setV={setV} k="security_deposit_amount" lbl="Security Deposit ₹" type="number" />
          <LeaseField f={f} setV={setV} k="deposit_paid_by" lbl="Deposit Paid By" options={['', 'MFPL', 'Franchisee', 'Other']} />
          <LeaseField f={f} setV={setV} k="current_deposit_holder" lbl="Current Deposit Holder" />
        </div>
      </Section>

      {/* D. Rent */}
      <Section title="D. Rent up to Agreed Exit Date">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <LeaseField f={f} setV={setV} k="rent_period_from" lbl="Rent Period From" type="date" />
          <LeaseField f={f} setV={setV} k="rent_period_to" lbl="Rent Period To" type="date" />
          <LeaseField f={f} setV={setV} k="gross_rent" lbl="Gross Rent ₹" type="number" />
          <LeaseField f={f} setV={setV} k="tds_applicable" lbl="TDS Applicable" options={YN} />
          <LeaseField f={f} setV={setV} k="tds_amount" lbl="TDS Amount ₹" type="number" />
          <LeaseField f={f} setV={setV} k="gst_applicable" lbl="GST Applicable" options={YN} />
          <LeaseField f={f} setV={setV} k="gst_amount" lbl="GST Amount ₹" type="number" />
          <LeaseField f={f} setV={setV} k="maintenance_cam" lbl="Maintenance / CAM ₹" type="number" />
          <LeaseField f={f} setV={setV} k="utilities_charges" lbl="Utilities ₹" type="number" />
          <LeaseField f={f} setV={setV} k="other_approved_charges" lbl="Other Approved Charges ₹" type="number" />
          <LeaseField f={f} setV={setV} k="total_legitimate_dues" lbl="Total Legitimate Dues ₹" type="number" />
          <LeaseField f={f} setV={setV} k="amount_already_paid" lbl="Amount Already Paid ₹" type="number" />
          <LeaseField f={f} setV={setV} k="balance_payable" lbl="Balance Payable ₹" type="number" />
          <LeaseField f={f} setV={setV} k="paid_payable_by" lbl="Paid / Payable By" options={['', 'MFPL', 'Franchisee', 'Operational Settlement', 'Other']} />
          <LeaseField f={f} setV={setV} k="payment_status" lbl="Payment Status" options={PAY_STATUS} />
          <LeaseField f={f} setV={setV} k="landlord_claim" lbl="Landlord Claim (as claimed)" wide />
          <LeaseField f={f} setV={setV} k="franchisee_confirmation" lbl="Franchisee Confirmation" wide />
          <LeaseField f={f} setV={setV} k="mfpl_verification" lbl="MFPL Verification" wide />
          <LeaseField f={f} setV={setV} k="supporting_document" lbl="Supporting Document (reference / URL)" wide />
          <LeaseField f={f} setV={setV} k="final_approved_amount" lbl="Final Approved Amount ₹" type="number" />
        </div>
      </Section>

      {/* E. Landlord */}
      <Section title="E. Landlord Discussion / Confirmation">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <LeaseField f={f} setV={setV} k="landlord_discussion_held" lbl="Discussion Held" options={YN} />
          <LeaseField f={f} setV={setV} k="discussion_date" lbl="Discussion Date" type="date" />
          <LeaseField f={f} setV={setV} k="landlord_representative" lbl="Landlord Representative" />
          <LeaseField f={f} setV={setV} k="discussed_by" lbl="Discussed By (MFPL / Exit Manager)" />
          <LeaseField f={f} setV={setV} k="rent_reconciled_through" lbl="Rent reconciled through" wide />
          <LeaseField f={f} setV={setV} k="landlord_agrees_mfpl_release" lbl="Landlord agrees MFPL release" options={YNP} />
          <LeaseField f={f} setV={setV} k="written_confirmation_received" lbl="Written confirmation received" options={YN} />
          <LeaseField f={f} setV={setV} k="termination_release_doc_status" lbl="Termination / Release document" options={DOC_STATUS} />
          <LeaseField f={f} setV={setV} k="no_dues_certificate_status" lbl="No-Dues Certificate" options={DOC_STATUS} />
          <LeaseField f={f} setV={setV} k="landlord_acknowledgement_doc_status" lbl="Landlord acknowledgement" options={DOC_STATUS} />
          <LeaseField f={f} setV={setV} k="landlord_remarks" lbl="Remarks" wide />
        </div>
      </Section>

      {/* F. Security Deposit */}
      <Section title="F. Security Deposit">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <LeaseField f={f} setV={setV} k="sd_original_amount" lbl="Original Security Deposit ₹" type="number" />
          <LeaseField f={f} setV={setV} k="sd_paid_by" lbl="Paid By" />
          <LeaseField f={f} setV={setV} k="sd_current_holder" lbl="Current Holder" />
          <LeaseField f={f} setV={setV} k="sd_refundable_amount" lbl="Refundable Amount ₹" type="number" />
          <LeaseField f={f} setV={setV} k="sd_deductions_claimed" lbl="Deductions Claimed ₹" type="number" />
          <LeaseField f={f} setV={setV} k="sd_deductions_approved" lbl="Approved Deductions ₹" type="number" />
          <LeaseField f={f} setV={setV} k="sd_refund_recipient" lbl="Refund Recipient" />
          <LeaseField f={f} setV={setV} k="sd_refund_status" lbl="Refund Status" options={['', 'PENDING', 'PART', 'RECEIVED', 'DISPUTED']} />
          <LeaseField f={f} setV={setV} k="sd_expected_refund_date" lbl="Expected Refund Date" type="date" />
          <LeaseField f={f} setV={setV} k="sd_disputes" lbl="Deposit disputes" wide />
        </div>
      </Section>

      {/* G. Tax */}
      <Section title="G. TDS / GST / Tax Documentation">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <LeaseField f={f} setV={setV} k="landlord_pan" lbl="PAN of Landlord" />
          <LeaseField f={f} setV={setV} k="landlord_gstin" lbl="GSTIN" />
          <LeaseField f={f} setV={setV} k="gst_invoice_received" lbl="GST Invoice Received" options={YN} />
          <LeaseField f={f} setV={setV} k="tds_applicable_flag" lbl="TDS Applicable" options={YN} />
          <LeaseField f={f} setV={setV} k="tds_deducted_amount" lbl="TDS Deducted ₹" type="number" />
          <LeaseField f={f} setV={setV} k="tds_deposited" lbl="TDS Deposited" options={YN} />
          <LeaseField f={f} setV={setV} k="tds_certificate_status" lbl="TDS Certificate / Form 16A" options={DOC_STATUS} />
          <LeaseField f={f} setV={setV} k="tax_discrepancy_notes" lbl="Tax discrepancy notes" wide />
        </div>
      </Section>

      {/* H. Utilities */}
      <Section title="H. Utilities & Premises Accounts">
        {(utils || []).length === 0 && <p className="text-xs text-slate-500 mb-2">No utility accounts yet.</p>}
        {(utils || []).map((u, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 items-center mb-1" data-testid={`lease-util-${i}`}>
            <Input placeholder="Account (Electricity/Water/…)" value={u.account_name || ''} onChange={(e) => setUtilRow(i, { account_name: e.target.value })} className="col-span-2 h-7 text-xs" />
            <Input placeholder="Number" value={u.account_number || ''} onChange={(e) => setUtilRow(i, { account_number: e.target.value })} className="col-span-2 h-7 text-xs" />
            <Input placeholder="Holder" value={u.current_holder || ''} onChange={(e) => setUtilRow(i, { current_holder: e.target.value })} className="col-span-2 h-7 text-xs" />
            <Input type="number" placeholder="Final bill ₹" value={u.final_bill ?? 0} onChange={(e) => setUtilRow(i, { final_bill: Number(e.target.value || 0) })} className="col-span-2 h-7 text-xs text-right" />
            <Select value={u.transfer_closure || 'PENDING'} onValueChange={(v) => setUtilRow(i, { transfer_closure: v })}>
              <SelectTrigger className="col-span-2 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{['PENDING', 'TRANSFERRED', 'CLOSED', 'RETAINED', 'N.A.'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={u.status || 'PENDING'} onValueChange={(v) => setUtilRow(i, { status: v })}>
              <SelectTrigger className="col-span-1 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{['PENDING', 'DONE', 'DISPUTED'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="icon" variant="ghost" className="col-span-1 h-6 w-6 text-red-500" onClick={() => delUtil(i)}><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={addUtil} data-testid="lease-util-add"><Plus className="w-3 h-3 mr-1" /> Add utility</Button>
      </Section>

      {/* I. Physical handover */}
      <Section title="I. Physical Premises Handover">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <LeaseField f={f} setV={setV} k="keys_handed_over" lbl="Keys handed over" options={YN} />
          <LeaseField f={f} setV={setV} k="keys_count" lbl="Number of Keys" type="number" />
          <LeaseField f={f} setV={setV} k="access_cards" lbl="Access Cards" />
          <LeaseField f={f} setV={setV} k="locks_digital_access" lbl="Locks / Digital Access" />
          <LeaseField f={f} setV={setV} k="premises_condition" lbl="Premises Condition" wide />
          <LeaseField f={f} setV={setV} k="landlord_inspection_completed" lbl="Landlord Inspection Completed" options={YN} />
          <LeaseField f={f} setV={setV} k="damage_claim_amount" lbl="Damage Claim ₹" type="number" />
          <LeaseField f={f} setV={setV} k="damage_approved_amount" lbl="Approved Damage ₹" type="number" />
          <LeaseField f={f} setV={setV} k="restoration_required" lbl="Restoration Required" options={YN} />
          <LeaseField f={f} setV={setV} k="restoration_completed" lbl="Restoration Completed" options={YN} />
          <LeaseField f={f} setV={setV} k="signage_removed" lbl="Signage Removed" options={YN} />
          <LeaseField f={f} setV={setV} k="internal_branding_removed" lbl="Internal Branding Removed" options={YN} />
          <LeaseField f={f} setV={setV} k="external_branding_removed" lbl="External Branding Removed" options={YN} />
          <LeaseField f={f} setV={setV} k="photographic_evidence_status" lbl="Photographic Evidence" options={DOC_STATUS} />
        </div>
      </Section>

      {/* J. Indemnity */}
      <Section title="J. Franchisee Indemnity / Future Liability">
        <div className="flex items-center gap-2 mb-2">
          <Checkbox checked={!!f.indemnity_acknowledged} onCheckedChange={(v) => setV('indemnity_acknowledged', !!v)} data-testid="lease-indemnity-ack" />
          <span className="text-xs text-slate-600">Franchisee acknowledges the indemnity clause as recorded in section J of Annexure E.</span>
        </div>
        <Textarea value={f.indemnity_notes || ''} onChange={(e) => setV('indemnity_notes', e.target.value)} placeholder="Additional notes / carve-outs (optional)" className="text-xs" data-testid="lease-indemnity-notes" />
      </Section>

      {/* K. Timeline */}
      <Section title="K. Stipulated Completion Timeline">
        {timeline.length === 0 && (
          <Button size="sm" variant="outline" onClick={seedTimelines} className="mb-2" data-testid="lease-timeline-seed">
            <RefreshCw className="w-3 h-3 mr-1" /> Seed default tasks
          </Button>
        )}
        {timeline.map((t, i) => (
          <div key={i} className="grid grid-cols-12 gap-1 items-center mb-1" data-testid={`lease-timeline-${i}`}>
            <Input placeholder="Task" value={t.task || ''} onChange={(e) => setTimelineRow(i, { task: e.target.value })} className="col-span-3 h-7 text-xs" />
            <Input placeholder="Owner" value={t.owner || ''} onChange={(e) => setTimelineRow(i, { owner: e.target.value })} className="col-span-2 h-7 text-xs" />
            <Input type="date" value={t.due_date || ''} onChange={(e) => setTimelineRow(i, { due_date: e.target.value })} className="col-span-2 h-7 text-xs" />
            <Select value={t.status || 'PENDING'} onValueChange={(v) => setTimelineRow(i, { status: v })}>
              <SelectTrigger className="col-span-1 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{['PENDING', 'DONE', 'DELAYED', 'N.A.'].map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Proof" value={t.proof || ''} onChange={(e) => setTimelineRow(i, { proof: e.target.value })} className="col-span-2 h-7 text-xs" />
            <Input placeholder="Verified by" value={t.verified_by || ''} onChange={(e) => setTimelineRow(i, { verified_by: e.target.value })} className="col-span-1 h-7 text-xs" />
            <Button size="icon" variant="ghost" className="col-span-1 h-6 w-6 text-red-500" onClick={() => delTimeline(i)}><Trash2 className="w-3 h-3" /></Button>
          </div>
        ))}
        <Button size="sm" variant="outline" onClick={() => addTimeline('')} data-testid="lease-timeline-add"><Plus className="w-3 h-3 mr-1" /> Add task</Button>
      </Section>

      {/* M. Sign-off */}
      <Section title="M. Sign-off">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
          <LeaseField f={f} setV={setV} k="signoff_landlord_name" lbl="Landlord — Name" />
          <LeaseField f={f} setV={setV} k="signoff_landlord_date" lbl="Landlord — Date" type="date" />
          <LeaseField f={f} setV={setV} k="signoff_franchisee_name" lbl="Franchisee — Name" />
          <LeaseField f={f} setV={setV} k="signoff_franchisee_date" lbl="Franchisee — Date" type="date" />
          <LeaseField f={f} setV={setV} k="signoff_mfpl_name" lbl="MFPL — Authorised Signatory" />
          <LeaseField f={f} setV={setV} k="signoff_mfpl_date" lbl="MFPL — Date" type="date" />
          <LeaseField f={f} setV={setV} k="signoff_exit_manager_name" lbl="Exit Manager — Name" />
          <LeaseField f={f} setV={setV} k="signoff_exit_manager_date" lbl="Exit Manager — Date" type="date" />
          <LeaseField f={f} setV={setV} k="landlord_signoff_pending_reason" lbl="If Landlord unavailable / refused — record reason" wide />
        </div>
      </Section>

      {/* Complete + save */}
      <div className="flex items-center justify-between p-2 bg-slate-50 rounded border sticky bottom-0">
        <label className="flex items-center gap-1 text-xs">
          <Checkbox checked={!!f.marked_complete} onCheckedChange={(v) => setV('marked_complete', !!v)} data-testid="lease-complete" />
          <span>Mark Annexure E complete <span className="text-[10px] text-slate-500">(requires effective RAG = GREEN or GREEN — SA OVERRIDE)</span></span>
        </label>
        <div className="flex items-center gap-2">
          {dirty && <span className="text-[11px] text-amber-600">Unsaved changes</span>}
          <Button size="sm" onClick={save} disabled={saving} data-testid="lease-save">
            {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle className="w-3 h-3 mr-1" />}
            Save Annexure E
          </Button>
        </div>
      </div>

      {eff !== 'GREEN' && eff !== 'GREEN_OVERRIDE' && (
        <div className="p-2 border border-amber-300 rounded bg-amber-50 text-xs flex items-start gap-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 text-amber-600" />
          <span>Annexure E is <b>{eff}</b>. A &quot;RENTAL / TRIPARTITE RELEASE — RESERVED / OPEN MATTER&quot; row is auto-carried into Annexure K (Open / Reserved Matters). The Exit Certificate cannot be issued until this is resolved.</span>
        </div>
      )}
      {eff === 'GREEN_OVERRIDE' && (
        <div className="p-2 border border-emerald-300 rounded bg-emerald-50 text-xs flex items-start gap-2" data-testid="lease-override-banner">
          <CheckCircle className="w-3 h-3 mt-0.5 text-emerald-700" />
          <span>Annexure E is <b>GREEN — SA OVERRIDE</b>. Any unresolved lease / tripartite matter remains recorded in Annexure K as <b>Accepted Under SA Override</b>. The Exit Certificate is no longer blocked by this section.</span>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="border rounded">
      <button type="button" onClick={() => setOpen(o => !o)} className="w-full text-left px-2 py-1 flex items-center gap-1 bg-slate-50">
        {open ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
        <span className="text-xs font-semibold text-slate-700">{title}</span>
      </button>
      {open && <div className="p-2">{children}</div>}
    </div>
  );
}

/* ── Certificate readiness banner ─────────────────────────────── */
export function CertificateReadinessBanner({ exitId, token, refreshKey, onOpenChecklists, onOpenSettlement, isSA = false, onEligibilityChange }) {
  const [status, setStatus] = useState(null);
  const [showDiag, setShowDiag] = useState(false);
  const load = useCallback(async () => {
    try {
      const s = await api(`/exit-status/${exitId}`, token);
      setStatus(s);
      if (onEligibilityChange) onEligibilityChange(!!s.can_issue_certificate, s);
    } catch (e) { /* silent */ }
  }, [exitId, token, onEligibilityChange]);
  useEffect(() => { load(); }, [load, refreshKey]);

  if (!status) return null;
  const ready = status.can_issue_certificate;
  const blockers = status.blockers || [];
  return (
    <div className={`border rounded-lg p-3 mb-3 ${ready ? 'bg-emerald-50 border-emerald-300' : 'bg-amber-50 border-amber-300'}`} data-testid="cert-readiness-banner">
      <div className="flex items-center gap-2">
        {ready ? <CheckCircle className="w-4 h-4 text-emerald-600" /> : <AlertTriangle className="w-4 h-4 text-amber-600" />}
        <span className="font-medium">
          {ready
            ? 'Exit ready — final certificate can be issued.'
            : `Certificate blocked — ${blockers.length} item${blockers.length === 1 ? '' : 's'}`}
        </span>
        {!ready && isSA && (
          <Button size="sm" variant="outline" className="ml-2 border-amber-500 text-amber-800 h-6 text-xs" onClick={() => setShowDiag(true)} data-testid="cert-why-blocked">
            Why Blocked?
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={load} className="ml-auto"><RefreshCw className="w-3 h-3" /></Button>
      </div>
      {!ready && (
        <div className="mt-2 text-xs text-amber-800 space-y-1" data-testid="cert-blockers-list">
          {blockers.length > 0 ? (
            blockers.slice(0, 5).map((b, idx) => (
              <div key={idx}>
                • <strong>{b.label}:</strong> {b.message}{' '}
                {b.key === 'financial_settlement' && (
                  <Button size="sm" variant="link" className="h-4 p-0 text-xs underline" onClick={onOpenSettlement}>Open settlement</Button>
                )}
                {(b.key === 'lease' || b.key === 'open_matters' || b.severity === 'HARD' && b.key !== 'financial_settlement' && b.key !== 'asset_handover') && (
                  <Button size="sm" variant="link" className="h-4 p-0 text-xs underline" onClick={onOpenChecklists}>Open checklists</Button>
                )}
              </div>
            ))
          ) : (
            <div>• Recalculating…</div>
          )}
        </div>
      )}

      {/* SA-only structured diagnostic */}
      <Dialog open={showDiag} onOpenChange={setShowDiag}>
        <DialogContent className="max-w-lg" data-testid="cert-diag-modal">
          <DialogHeader>
            <DialogTitle>Certificate Eligibility Diagnostic</DialogTitle>
            <DialogDescription className="text-xs text-slate-700">
              Live status of every gate that controls the Final Exit Certificate.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 text-sm">
            <DiagRow label="Financial Settlement" ok={status.financial_settlement_finalized} />
            <DiagRow label="Asset Handover" ok={status.asset_handover_finalized} />
            {(status.sections || []).map(s => (
              <DiagRow key={s.section} label={s.label || s.section} ok={!!s.completed} note={s.rag_effective ? `RAG: ${s.rag_effective}` : undefined} />
            ))}
            <DiagRow
              label="Open Matters (hard blockers)"
              ok={(status.open_matters_hard_blockers || 0) === 0}
              note={`${status.open_matters_open || 0} open (${status.open_matters_hard_blockers || 0} hard)`}
            />
            <DiagRow
              label="Food Inventory (informational)"
              ok={true}
              note={`${status.zero_rate_exceptions || 0} unpriced — non-blocking (iter225)`}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { load(); setShowDiag(false); }}>Refresh & Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DiagRow({ label, ok, note }) {
  return (
    <div className="flex items-center gap-2">
      {ok
        ? <CheckCircle className="w-4 h-4 text-emerald-600" />
        : <AlertTriangle className="w-4 h-4 text-red-600" />}
      <span className={ok ? 'text-slate-700' : 'text-red-800 font-medium'}>{label}</span>
      {note && <span className="text-[11px] text-slate-500 ml-auto">{note}</span>}
    </div>
  );
}


/* ══════════════════════════════════════════════════════════════════════
 * iter191 — Annexure H (Employee Salary Settlement) Preview panel
 * ─────────────────────────────────────────────────────────────────────
 * Reads /settlement/employees/preview which is the single source of
 * truth shared with the PDF (and, later, the Word) renderer, so the
 * three views stay identical.  Actions: Seed (auto-populate from
 * Attendance + Payroll), Refresh, Freeze on closure.
 * ═════════════════════════════════════════════════════════════════ */
export function AnnexureHPreviewModal({ open, onOpenChange, exitId, token }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [monthDraft, setMonthDraft] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/settlement/employees/preview/${exitId}`, token);
      setData(r);
      setMonthDraft(r.salary_month || '');
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [exitId, token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const seed = async () => {
    if (!window.confirm('Auto-populate Annexure H from HR Salary for the selected month? Existing rows are preserved.')) return;
    setLoading(true);
    try {
      const r = await api(`/settlement/employees/seed/${exitId}`, token,
        { salary_month: monthDraft || undefined });
      if (r.success === false) toast.info(r.message || 'Skipped');
      else toast.success(`${r.created || 0} employees seeded from HR (${r.hr_snapshot_state || 'LIVE'})`);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const resync = async () => {
    if (!window.confirm('Force resync from HR Salary?\n\nThis will DELETE the current auto-populated rows and re-fetch fresh data from HR Management → Salary → this centre → the selected month.\n\nAny manually entered rows will be preserved.')) return;
    setLoading(true);
    try {
      const r = await api(`/settlement/employees/seed/${exitId}`, token,
        { salary_month: monthDraft || undefined, resync: true,
          reason: 'Manual resync from Annexure H preview' });
      if (r.success === false) toast.info(r.message || 'Nothing to resync');
      else toast.success(`Re-synced ${r.created || 0} rows from HR (${r.hr_snapshot_state || 'LIVE'})`);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const changeMonth = async () => {
    if (!monthDraft || monthDraft.length !== 7) { toast.error('Format: YYYY-MM'); return; }
    if (monthDraft === (data?.salary_month || '')) return;
    if (!window.confirm(`Change salary month to ${monthDraft}? Auto-seeded rows will be cleared so the new month can be reloaded from HR.`)) return;
    setLoading(true);
    try {
      await api(`/settlement/employees/set-salary-month/${exitId}`, token,
        { salary_month: monthDraft });
      toast.success(`Salary month set to ${monthDraft}`);
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const freeze = async () => {
    const reco = data?.hr_reconciliation;
    let override = false, reason = '';
    if (reco && reco.status !== 'MATCHED') {
      reason = window.prompt(`Annexure H total (₹${reco.annexure_h_total_payable}) does not match HR Payroll total (₹${reco.hr_total_net}). Difference ₹${reco.difference}.\n\nEnter a reason to override and proceed (SA only):`);
      if (!reason || !reason.trim()) return;
      override = true;
    } else if (!data?.payroll_finalised) {
      reason = window.prompt('HR payroll for this month is not FINAL. Enter a reason to proceed with a LIVE snapshot (SA only):');
      if (!reason || !reason.trim()) return;
      override = true;
    } else {
      if (!window.confirm('Freeze Annexure H figures? After closure the values cannot silently recompute.')) return;
    }
    setLoading(true);
    try {
      await api(`/settlement/employees/freeze/${exitId}`, token,
        { override_mismatch: override, reason });
      toast.success('Annexure H frozen at closure');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  if (!open) return null;
  const rows = data?.rows || [];
  const t = data?.totals || {};
  const frozen = !!data?.frozen;
  const independent = !!data?.independent;
  const locked = frozen || independent;
  const reco = data?.hr_reconciliation;
  const anyNotFinalised = data?.payroll_not_finalised_flagged;
  const finalStatusColor = t.final_status === 'CLEARED'
    ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
    : 'bg-amber-100 text-amber-800 border-amber-300';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto" data-testid="annexure-h-preview-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Annexure H — Employee Salary Settlement (Preview)
            {!frozen && !data?.reopened && <Badge className="bg-amber-100 text-amber-700 border-amber-200">DRAFT PREVIEW · NOT FINALISED</Badge>}
            {!frozen && data?.reopened && <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid="annexure-h-preview-reopened">REOPENED · revision v{(data?.approved_version || 0) + 1} in progress · PDF uses v{data?.approved_version}</Badge>}
            {frozen && <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 gap-1"><Lock className="w-3 h-3" /> Finalised{data?.version ? ` · v${data.version}` : ""}</Badge>}
          </DialogTitle>
          <DialogDescription>
            {independent
              ? <>Independent exit settlement edited by Super Admin (initially imported from HR Salary → {data?.center || '—'} → {data?.salary_month || '—'}). Payroll changes do not overwrite it — edit it in the Annexure H section.</>
              : <>Pulled directly from <b>HR Management → Salary → {data?.center || '—'} → {data?.salary_month || '—'}</b>.</>} Same data appears in the PDF and Word Exit Agreement. Salaries already inside operating expenses are not deducted again in the settlement.
          </DialogDescription>
        </DialogHeader>

        {/* iter193 — HR Salary Month selector + reconciliation banner */}
        {!locked && <div className="p-2 border rounded bg-slate-50 mb-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
          <div>
            <label className="text-[11px] text-slate-500 block">Exit Salary Month</label>
            <Input type="month" value={monthDraft} onChange={(e) => setMonthDraft(e.target.value)} className="h-8 text-xs" disabled={frozen} data-testid="annexure-h-month-input" />
            <p className="text-[10px] text-slate-500 mt-0.5">Default = month containing closure date ({data?.closure_month || '—'})</p>
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" variant="outline" onClick={changeMonth} disabled={loading || frozen || !monthDraft} data-testid="annexure-h-month-apply">
              Apply Month
            </Button>
            <span className="text-[11px] text-slate-500">HR State: <b>{data?.payroll_state || '—'}</b>{data?.payroll_finalised ? ' (FINAL)' : ' (not final)'}</span>
          </div>
          {reco && (
            <div className={`p-2 rounded border text-[11px] ${reco.status === 'MATCHED' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`} data-testid="annexure-h-reconciliation">
              <div className="font-semibold mb-0.5">{reco.status}</div>
              <div>HR: ₹{Number(reco.hr_total_net || 0).toLocaleString('en-IN')} · Annexure: ₹{Number(reco.annexure_h_total_payable || 0).toLocaleString('en-IN')} · Diff: ₹{Number(reco.difference || 0).toLocaleString('en-IN')}</div>
            </div>
          )}
        </div>}

        {anyNotFinalised && (
          <div className="p-2 mb-3 border border-red-300 rounded bg-red-50 text-xs text-red-800 flex items-start gap-2" data-testid="annexure-h-not-final-banner">
            <AlertTriangle className="w-3 h-3 mt-0.5" />
            <span>HR payroll for <b>{data?.salary_month}</b> is not FINAL. Rows show <b>PAYROLL NOT FINALISED</b>. Lock the HR payroll first — or record an SA override on Freeze.</span>
          </div>
        )}

        {!locked && <div className="flex items-center gap-2 mb-3">
          <Button size="sm" variant="outline" onClick={seed} disabled={loading || frozen} data-testid="annexure-h-seed">
            <RefreshCw className="w-3 h-3 mr-1" /> Auto-populate from HR
          </Button>
          <Button size="sm" variant="outline" onClick={resync} disabled={loading || frozen} data-testid="annexure-h-resync">
            <RefreshCw className="w-3 h-3 mr-1" /> Re-sync from HR (wipe stale)
          </Button>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="annexure-h-refresh">
            Refresh
          </Button>
          <div className="flex-1" />
          <Button size="sm" onClick={freeze} disabled={loading || frozen || rows.length === 0} data-testid="annexure-h-freeze">
            <Lock className="w-3 h-3 mr-1" /> Freeze on Closure
          </Button>
        </div>}

        {loading ? (
          <div className="py-12 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 border border-dashed rounded text-center text-sm text-slate-500">
            No employees recorded for this exit yet. Click <b>Auto-populate</b> to seed from Attendance + Payroll.
          </div>
        ) : (
          <>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm" data-testid="annexure-h-table">
                <thead className="bg-slate-50 text-slate-700 text-xs">
                  <tr>
                    <th className="px-2 py-2 text-left">Employee Name</th>
                    <th className="px-2 py-2 text-left">Designation</th>
                    <th className="px-2 py-2 text-right">Days Worked</th>
                    <th className="px-2 py-2 text-right">Salary Payable ₹</th>
                    <th className="px-2 py-2 text-right">Paid ₹</th>
                    <th className="px-2 py-2 text-right">Balance ₹</th>
                    <th className="px-2 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const payable = Number(r.salary_payable || 0);
                    const paid = Number(r.salary_paid || 0);
                    const balance = Math.round((payable - paid) * 100) / 100;
                    const notFinal = !!r.payroll_not_finalised;
                    const st = notFinal ? 'PENDING' : (r.payment_status || (balance <= 0.01 ? 'CLEARED' : 'PENDING'));
                    return (
                      <tr key={r.row_id || i} className={`border-t ${notFinal ? 'bg-red-50/60' : ''}`} data-testid={`annexure-h-row-${i}`}>
                        <td className="px-2 py-1.5">{r.name}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.designation || '—'}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{Number(r.days_worked || 0)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {notFinal ? <span className="text-red-700 font-semibold text-[10px]">PAYROLL NOT FINALISED</span> : inr(payable)}
                        </td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{notFinal ? '—' : inr(paid)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{notFinal ? '—' : inr(balance)}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${st === 'CLEARED' ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-amber-100 text-amber-800 border-amber-300'}`}>
                            {st}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-slate-50 border-t-2 border-slate-300 text-xs font-semibold">
                  <tr>
                    <td className="px-2 py-2" colSpan={3}>Total Employees: {t.total_employees ?? 0}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{inr(t.total_salary_payable)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{inr(t.total_salary_paid)}</td>
                    <td className="px-2 py-2 text-right tabular-nums">{inr(t.total_balance_payable)}</td>
                    <td className="px-2 py-2 text-center">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold border ${finalStatusColor}`} data-testid="annexure-h-final-status">
                        {t.final_status || 'PENDING'}
                      </span>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="mt-3 p-2 bg-slate-50 border rounded text-[11px] text-slate-600 leading-snug">
              <b>Source:</b> HR Management → Salary → <b>{data?.center || '—'}</b> → <b>{data?.salary_month || '—'}</b> ({data?.payroll_state || '—'}). Days Worked = HR <code>presentDays</code>; Salary Payable = HR <code>netSalary</code>. No separate calculation is performed inside Franchise Exit. Monthly MGT salary is excluded. <b>Financial treatment:</b> INCLUDED IN OPERATING EXPENSES — salaries already reflected in Sales Dashboard / Annexure A are <b>not</b> deducted again in the settlement. {frozen && (<><br /><b>Frozen at:</b> {data.frozen_at} · <b>by:</b> {data.frozen_by}</>)}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ══════════════════════════════════════════════════════════════════════
 * iter196 — Generic Draft Preview modal
 * ─────────────────────────────────────────────────────────────────────
 * Works AS SOON AS data has been saved — no Freeze / Sign / Finalise
 * required.  Reads /preview/section/{exit}/{section} and renders the
 * rows/payload in a DRAFT PREVIEW · NOT FINALISED frame.  Incomplete
 * sections visibly show PENDING / DATA MISSING.
 * ═════════════════════════════════════════════════════════════════ */

const _SECTION_LABEL = {
  lease: 'Rental / Tripartite Lease',
  debrand: 'Brand De-branding',
  social: 'Social Media',
  digital: 'Digital Presence',
  access: 'Access Removal',
  confidentiality: 'Confidentiality',
  compliance: 'Compliance',
  open_matters: 'Open / Reserved Matters',
  vendor_payables: 'Vendor / Utility Payables',
  settlement: 'Financial Settlement',
  food_inventory: 'Food Inventory (Annexure C)',
  asset_handover: 'Asset Handover',
};

function _fmt(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'YES' : 'NO';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export function DraftPreviewModal({ open, onOpenChange, exitId, token, section, isSA = false, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [nilRemarks, setNilRemarks] = useState('');

  const load = useCallback(async () => {
    if (!section) return;
    setLoading(true);
    try {
      const r = await api(`/preview/section/${exitId}/${section}`, token);
      setData(r);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [exitId, token, section]);

  useEffect(() => { if (open) { setNilRemarks(''); load(); } }, [open, load]);

  if (!open) return null;
  const rows = data?.rows || [];
  const seeded = !!data?.seeded;
  const completed = !!data?.completed;
  const frozen = !!data?.frozen;
  const overrideTag = data?.override === 'SUPER_ADMIN_OVERRIDE';
  const nilDeclared = !!data?.nil_declared;
  const autoFromSettlement = !!data?.auto_completed_from_settlement;
  const statusPill = frozen
    ? { text: overrideTag ? 'COMPLETED — SUPER ADMIN OVERRIDE' : 'FROZEN', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' }
    : completed
    ? { text: overrideTag
          ? 'COMPLETED — SUPER ADMIN OVERRIDE'
          : nilDeclared
          ? 'COMPLETED — NIL DECLARED'
          : autoFromSettlement
          ? 'COMPLETED — SETTLEMENT DERIVED'
          : 'COMPLETED · NOT FROZEN',
        cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' }
    : seeded
    ? { text: 'DRAFT · PENDING', cls: 'bg-amber-100 text-amber-800 border-amber-300' }
    : { text: 'DATA MISSING', cls: 'bg-red-100 text-red-800 border-red-300' };

  // FR-007 P0 — Custom human-readable renderers for settlement and
  // vendor_payables (Annexure I).  Everything else falls through to
  // the generic row renderer.
  const settlementView = data?.settlement_view;
  const annexureI = data?.annexure_i_view;

  const saveNil = async (declared) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/franchise-exit/vendor-payables/nil/${exitId}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, remarks: nilRemarks, declared }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      toast.success(declared ? 'NIL declaration saved' : 'NIL declaration cleared');
      load();
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const renderSettlement = () => {
    if (!settlementView) return null;
    const sv = settlementView;
    if (sv.correction_in_progress) {
      return (
        <div className="p-4 border border-amber-400 bg-amber-50 rounded text-sm">
          <b>Settlement correction in progress.</b> The frozen snapshot has been unlocked by Super Admin. Refresh from ERP and re-freeze the settlement to release this gate.
        </div>
      );
    }
    const _amt = (v, sign) => {
      if (v === null || v === undefined) return <span className="text-red-500">DATA PENDING</span>;
      return <span>{sign === '-' ? '(' : ''}{inr(v)}{sign === '-' ? ')' : ''}</span>;
    };
    return (
      <div className="space-y-3" data-testid="settlement-financial-table">
        {sv.data_repaired && (
          <div className="p-2 border border-blue-300 bg-blue-50 rounded text-xs">
            <b>DATA REPAIRED</b> — this snapshot was refreshed from ERP after finalisation. The original finalisation record is preserved.
          </div>
        )}
        <table className="w-full text-xs border rounded">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left">Description</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Source</th>
            </tr>
          </thead>
          <tbody>
            {sv.rows.map((r, i) => (
              <tr key={i} className="border-t">
                <td className="px-3 py-1.5">{r.sign === '+' ? '+ ' : r.sign === '-' ? '− ' : '  '}{r.label}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium">{_amt(r.amount, r.sign)}</td>
                <td className="px-3 py-1.5 text-slate-500 text-[10px]">{r.source}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-400 bg-slate-50">
              <td className="px-3 py-2 font-semibold">FINAL SETTLEMENT</td>
              <td className="px-3 py-2 text-right tabular-nums font-bold">{sv.final_amount === null || sv.final_amount === undefined ? <span className="text-red-500">NOT CALCULATED</span> : inr(sv.final_amount)}</td>
              <td className="px-3 py-2 text-slate-600 text-[10px] font-medium">{sv.verdict || '—'}</td>
            </tr>
          </tbody>
        </table>
        {sv.breakdown && sv.breakdown.length > 0 && (
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-600 font-medium">Head-by-head (informational — already inside operating expenses)</summary>
            <table className="w-full text-xs border rounded mt-2">
              <tbody>
                {sv.breakdown.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-1 text-slate-600">{r.label}</td>
                    <td className="px-3 py-1 text-right tabular-nums">{inr(r.amount || 0)}</td>
                    <td className="px-3 py-1 text-slate-400 text-[10px]">{r.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
        {sv.finalized && (
          <div className="text-[11px] text-slate-500">
            Finalised by <b>{sv.finalized_by || '—'}</b> at <b>{sv.finalized_at || '—'}</b>.
          </div>
        )}
      </div>
    );
  };

  const renderAnnexureI = () => {
    if (!annexureI) return null;
    const ai = annexureI;
    if (ai.nil_declared) {
      return (
        <div className="space-y-3">
          <div className="p-3 border-2 border-emerald-400 bg-emerald-50 rounded text-sm" data-testid="annexure-i-nil-badge">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-600" />
              <b>NO OUTSTANDING VENDOR / UTILITY PAYABLES</b>
            </div>
            <div className="mt-1 text-[11px] text-emerald-800">
              Declared by <b>{ai.nil_declared_by || '—'}</b> at <b>{ai.nil_declared_at || '—'}</b>
              {ai.nil_remarks && <> · <i>{ai.nil_remarks}</i></>}
            </div>
          </div>
          {isSA && (
            <Button variant="outline" size="sm" className="text-red-700 border-red-300" onClick={() => saveNil(false)} disabled={saving} data-testid="btn-clear-nil">
              Clear NIL declaration
            </Button>
          )}
        </div>
      );
    }
    return (
      <div className="space-y-3" data-testid="annexure-i-table">
        {ai.rows.length === 0 ? (
          <div className="p-3 border border-dashed border-slate-300 rounded text-sm text-slate-600">
            No vendor/utility payables auto-derived from the current settlement. If truly nothing is outstanding, save the NIL declaration below.
          </div>
        ) : (
          <table className="w-full text-xs border rounded">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="px-2 py-1.5 text-left">Type</th>
                <th className="px-2 py-1.5 text-left">Party</th>
                <th className="px-2 py-1.5 text-left">Description</th>
                <th className="px-2 py-1.5 text-right">Amount</th>
                <th className="px-2 py-1.5 text-left">Settlement Treatment</th>
                <th className="px-2 py-1.5 text-left">Remarks</th>
              </tr>
            </thead>
            <tbody>
              {ai.rows.map((r, i) => (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1"><span className="px-1.5 py-0.5 rounded bg-slate-100 text-[10px]">{r.payable_type}</span></td>
                  <td className="px-2 py-1">{r.party || '—'}</td>
                  <td className="px-2 py-1 text-slate-600">{r.description || '—'}</td>
                  <td className="px-2 py-1 text-right tabular-nums">{inr(r.amount)}</td>
                  <td className="px-2 py-1 text-slate-600 text-[10px]">{r.settlement_treatment}</td>
                  <td className="px-2 py-1 text-slate-500 text-[10px]">{r.remarks || '—'}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                <td className="px-2 py-1.5" colSpan={3}>TOTAL</td>
                <td className="px-2 py-1.5 text-right tabular-nums">{inr(ai.total_amount)}</td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>
        )}
        {isSA && !ai.nil_declared && ai.rows.length === 0 && (
          <div className="p-3 border border-slate-300 rounded space-y-2 bg-slate-50">
            <div className="text-xs font-medium text-slate-700">Save NIL Declaration</div>
            <Textarea
              placeholder="Remarks (optional)"
              value={nilRemarks}
              onChange={(e) => setNilRemarks(e.target.value)}
              className="text-xs"
              rows={2}
              data-testid="input-nil-remarks"
            />
            <Button size="sm" onClick={() => saveNil(true)} disabled={saving} data-testid="btn-save-nil" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              ☐ NO OUTSTANDING VENDOR / UTILITY PAYABLES — Save NIL Declaration
            </Button>
          </div>
        )}
      </div>
    );
  };

  // Generic row renderer for other sections (filters DB metadata + JSON arrays).
  const renderRow = (r, i) => {
    const payload = r?.payload && typeof r.payload === 'object' ? r.payload : null;
    const fields = payload || r;
    const skipKeys = ['exit_id','row_id','deleted','created_by','updated_by','created_at','updated_at','_id','_sources','_status','_settlement_draft','refreshed_at','refreshed_by','snapshot_version','previous_snapshot_version','erp_status','erp_sources','erp_totals'];
    const entries = Object.entries(fields).filter(([k, v]) => !k.startsWith('_') && !skipKeys.includes(k) && !(typeof v === 'object' && v !== null && !Array.isArray(v) && Object.keys(v).length > 10) && !(Array.isArray(v) && v.some(x => typeof x === 'object')));
    return (
      <div key={r.row_id || i} className="mb-2 p-2 border rounded bg-white">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
          {entries.map(([k, v]) => (
            <div key={k} className="flex gap-2">
              <span className="text-slate-500 min-w-[140px]">{k.replace(/_/g, ' ')}</span>
              <span className="text-slate-800 break-words">{_fmt(v)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const isCustomView = section === 'settlement' || section === 'vendor_payables';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid={`draft-preview-modal-${section}`}>
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <DialogTitle>{data?.label || _SECTION_LABEL[section] || section}</DialogTitle>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200">DRAFT PREVIEW · NOT FINALISED</Badge>
            <Badge variant="outline" className={statusPill.cls}>{statusPill.text}</Badge>
          </div>
          <DialogDescription className="text-[11px]">
            <span className="mr-3">Last Saved: <b>{data?.last_saved_at || '—'}</b></span>
            <span className="mr-3">Saved By: <b>{data?.last_saved_by || '—'}</b></span>
            <span>Row Count: <b>{isCustomView ? (section === 'settlement' ? (settlementView?.final_amount != null ? 1 : 0) : (annexureI?.row_count ?? 0)) : (data?.count ?? 0)}</b></span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : section === 'settlement' ? (
          renderSettlement() || (
            <div className="p-6 border border-dashed rounded text-center text-sm text-slate-500">
              <AlertTriangle className="w-4 h-4 inline mr-2 text-red-400" />
              <b>DATA MISSING</b> — no settlement data available yet.
            </div>
          )
        ) : section === 'vendor_payables' ? (
          renderAnnexureI()
        ) : !seeded ? (
          <div className="p-6 border border-dashed rounded text-center text-sm text-slate-500" data-testid={`draft-preview-empty-${section}`}>
            <AlertTriangle className="w-4 h-4 inline mr-2 text-red-400" />
            <b>DATA MISSING</b> — no rows saved for this section yet. Open the Checklists / relevant tile, enter and save data, then re-open this preview.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map(renderRow)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ══════════════════════════════════════════════════════════════════════
 * iter196 — Preview Complete Exit Pack
 * ─────────────────────────────────────────────────────────────────────
 * Renders the entire agreement's saved state — every section — with
 * PENDING / DATA MISSING flags so the SA can review before finalising.
 * ═════════════════════════════════════════════════════════════════ */

export function CompleteExitPackPreviewModal({ open, onOpenChange, exitId, token }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api(`/preview/complete-pack/${exitId}`, token);
      setData(r);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [exitId, token]);

  useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;
  const sections = data?.sections || [];
  const pending = data?.pending_sections || [];
  const missing = data?.data_missing_sections || [];
  const v2 = data?.v2 || {};
  const readiness = data?.readiness || {};
  const ready = !!readiness.can_issue_certificate;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="complete-exit-pack-modal">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <DialogTitle>Complete Exit Pack — Preview</DialogTitle>
            {ready ? (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">READY FOR FINAL CERTIFICATE</Badge>
            ) : pending.length > 0 || missing.length > 0 ? (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">DRAFT — {(pending.length || 0) + (missing.length || 0)} item{((pending.length || 0) + (missing.length || 0)) === 1 ? '' : 's'} remaining</Badge>
            ) : (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200">DRAFT PREVIEW · NOT FINALISED</Badge>
            )}
          </div>
          <DialogDescription className="text-[11px]">
            Shows the entire agreement using all currently saved data. Statuses reflect the SAME readiness engine that gates the Final Exit Certificate — the main-screen banner and this preview can never disagree.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-slate-400"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
        ) : (
          <div className="space-y-3">
            {/* Top-line reconciliations */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]">
              {v2.lease_rag && (
                <div className="p-2 border rounded bg-slate-50">
                  <div className="text-slate-500">Annexure E — Rental RAG</div>
                  <div className="font-semibold text-slate-800">{v2.lease_rag.effective || '—'}</div>
                </div>
              )}
              {v2.employees_reconciliation && (
                <div className={`p-2 border rounded ${v2.employees_reconciliation.status === 'MATCHED' ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="text-slate-500">Annexure H — HR Reconciliation ({v2.employees_salary_month || '—'})</div>
                  <div className="font-semibold text-slate-800">{v2.employees_reconciliation.status || '—'} · Diff ₹{Number(v2.employees_reconciliation.difference || 0).toLocaleString('en-IN')}</div>
                </div>
              )}
              {(v2.food_inventory_unpriced_count ?? 0) > 0 && (
                <div className="p-2 border rounded bg-slate-50 border-slate-200" data-testid="pack-unpriced-note">
                  <div className="text-slate-500">Annexure C — Valuation Note</div>
                  <div className="font-medium text-slate-700">{v2.food_inventory_unpriced_count} item(s) transferred without ERP rate — physical qty preserved</div>
                </div>
              )}
            </div>

            {(pending.length > 0 || missing.length > 0) && (
              <div className="p-2 border border-amber-300 rounded bg-amber-50 text-xs" data-testid="complete-pack-pending-banner">
                <b>Pending items:</b>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  {missing.map(s => <li key={`m-${s}`}><span className="text-red-700 font-semibold">DATA MISSING</span> — {s}</li>)}
                  {pending.filter(p => !missing.includes(p)).map(s => <li key={`p-${s}`}><span className="text-amber-700 font-semibold">PENDING</span> — {s}</li>)}
                </ul>
              </div>
            )}

            <div className="border rounded overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 text-slate-700">
                  <tr>
                    <th className="px-2 py-1.5 text-left">Section</th>
                    <th className="px-2 py-1.5 text-center">Rows</th>
                    <th className="px-2 py-1.5 text-center">Status</th>
                    <th className="px-2 py-1.5 text-left">Last Saved</th>
                    <th className="px-2 py-1.5 text-left">By</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map(s => {
                    const st = s.frozen ? 'FROZEN'
                      : s.override === 'SUPER_ADMIN_OVERRIDE' ? 'COMPLETED — SA OVERRIDE'
                      : s.nil_declared ? 'COMPLETED — NIL'
                      : s.auto_completed_from_settlement ? 'COMPLETED'
                      : s.completed ? 'COMPLETED'
                      : s.seeded ? 'PENDING' : 'DATA MISSING';
                    const cls = (s.frozen || s.completed) ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
                      : s.seeded ? 'bg-amber-100 text-amber-800 border-amber-300'
                      : 'bg-red-100 text-red-800 border-red-300';
                    return (
                      <tr key={s.section} className="border-t" data-testid={`complete-pack-row-${s.section}`}>
                        <td className="px-2 py-1.5">{s.label}</td>
                        <td className="px-2 py-1.5 text-center tabular-nums">{s.count ?? 0}</td>
                        <td className="px-2 py-1.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full border ${cls}`}>{st}</span>
                        </td>
                        <td className="px-2 py-1.5 text-slate-500">{s.last_saved_at || '—'}</td>
                        <td className="px-2 py-1.5 text-slate-500">{s.last_saved_by || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
